import { withNewWaBoardStatus } from "@common-reducers/with-new-wa-board-status";
import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { Bot } from "@/common/models/bot";
import { selectOnlineEngines } from '@common/reducers/ConnectedEnginesSlice';
import { createDeepEqualSelector } from "@/common/services/deep-equal-selector";
import { deleteUserMessage, getPendingUserMessages } from "@/common/thunks/UserMessagesThunks";
import {
  captureMessage as vaultCaptureMessage,
  getMessage as vaultGetMessage,
  getMostRecentInChat as vaultGetMostRecentInChat,
  getNearestInChatByTimestamp as vaultGetNearestByTs,
  buildVaultRowFromWaMessage,
  resolveWaMsgSerializedId,
  queryVaultMessages,
  countVaultMessages,
  vaultRowToMessageRow,
  vaultTopUp,
  vaultBrowseWindow,
  withVaultPaging,
  pushDeletionFromVaultRow,
  pushRecentDeletion,
} from "@common/services/message-vault";
import { isVaultCaptureEnabled } from "@extension/services/vault-backup/backup-config-selectors";
import { fetchRule } from "@common-components/schedule-picker/recurrence-utils";
import { ContactInfo } from "@common-models/contact-info";
import { Person } from "@common-models/person";
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { ExtendedUserMessage } from '@common/models/extend-user-message';
import { getSortGroup } from "@common/utils/contact-sort-utils";
import { getWaChatContactDisplayName } from "@/common/utils/display-name-utils";
import { buildDripReplyIds } from "./drip-reply-ids";
import { isCancelIfReceivedTrigger } from "./cancel-if-received-trigger";
import { newsletterChatsToCacheEntities, chatsToContactCacheEntities } from "./newsletter-cache-entities";

import { botDeploymentSelectOneObjectByQuery, botSelectByQuery, contactsLabelsSelectByQuery, contactsLabelsSelectOneObjectByQuery, DBBoardsThunks, DBContactsLabelsThunks, DBUserMessageThunks, DBUserThunks, DBWaContactsCacheThunks, labelSelectOneObjectByQuery, waContactsCacheSelectors, WaChatReducerActions, WaContactsCacheReducerActions } from "@common-reducers/DBServiceThunks";
import { isBotRelevantForSender } from "@common/utils/trigger-analysis";
import { resolveSenderJidEquivalents } from "@common/utils/jid-equivalents";
import { AnalyticsService } from "@common-services/analytics-service";
import { blobMap } from '@common-services/blob-service'; // Adjust import for blobMap if needed
import { normalizeFileMetadata } from '@common-services/file-utils';
import { isWhatsappExtensionContext, isExtensionContext } from "@common-services/detect-context";
import { isAiLinkedChat } from "@extension-context-content/services/ai-linked-chats-registry";
import { postMessageWithResponse, isWireError, type WireResponse } from "@common-services/post-message-to-whatsapp-functions";
import { accessFormPopupActions } from "@common/reducers/AccessFormPopupReducer";
import { addScheduledMessageButtonActions } from "@common/reducers/AddScheduledMessageButtonReducer";
import { boardsActions } from "@common/reducers/BoardsReducer";
import { getBoardByChat } from "@common/reducers/BoardsSelectors";
import { selectIsWhatsappInitialized } from "@common/reducers/ConnectionReadinessSlice";
import { notifierSubscribeThunk } from "@/common/noifier-module/NotifierThunks";
import { getProfilePicThunk } from "@common/reducers/ProfilePicReducer";
import { profilePicFetchRegistry } from "@extension/context/content/hooks/profilePicFetchRegistry";
import { createInFlightShare } from "@common/utils/in-flight-share";
import { setContactPicMap } from "@common/reducers/CalendarReducer";
import { updateBoardPersonListThunk } from "@common/reducers/TaskThunks";
import { selectUser } from "@common/reducers/UserSelectors";
import { privateSetCurrentChat, selectMergedContactList, selectMergedContactMap, setAppState, setImportedContactsArray, setInvalidWANumber } from "@common/reducers/WhatsAppReducer";
import { setStartChatWithPhoneNumberOpen } from "@common/reducers/MainReducer";
import { selectFilteredContacts, selectProfilePic } from "@common/reducers/WhatsAppSelectors";
import { getWaContactBySerializedId } from "@common/reducers/wa-contact-entity-lookup";
import { getArchivedContactIds } from "@/common/reducers/labels-reducer/selectors";
import { filterChatsByLabelIdThunk } from "@/common/reducers/labels-reducer/labels-thunks";
import { patchEntity, backendApi } from "@common/reducers/backend-api/backend-api";
import { dispatchWaEventThunk } from './WaEventDispatchThunk';
// Import directly from client-actions to avoid circular dependency with index.ts
import { WhatsappToolsClientActions } from "@common/tools/whatsapp/wa-tools-client-actions";
import { triggerRequesthWhatsappTool, extractToolErrorMessage } from "@common/tools/whatsapp/wa-tools-server-actions";
import { validateChatIdForTool } from "@common/tools/whatsapp/wa-chat-id-validation";
import { WA_TOOL_COMMANDS, waToolSuccess, waToolError, unwrapEngineResult, EXTENSION_INTERNAL_COMMANDS } from "../../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type { WaToolResponse } from "../../../../bt-common/interfaces/whatsapp-tools-commands.interface";
import type { WaToolArgs, WaToolResult, WaToolCommand } from "../../../../bt-common/interfaces/whatsapp-tool-contracts.interface";
import { WaGetMessageMediaPageResultSchema, resolveLimit } from "../../../../bt-common/interfaces/whatsapp-tool-contracts.interface";
import { extractPdfTextInContentScript } from "@extension/services/pdf-extraction";
import { uploadMediaFile } from "@extension/services/media-upload";
import { resizeImageForAgentVision } from "@extension/utils/agent-vision-resize";
import {
    classifyDocTextToKind,
    docKindToFileKind,
    looksLikeGeneratorMetadata,
    type FileIntelResult,
    type FileKind,
} from "@common/services/file-intel-helpers";
import { postAction } from "../services/actions-fetch";

/**
 * Safe field-pick helpers for narrowing `unknown` page-handler emits to
 * the canonical contract Result shape. Used by the few legacy tools
 * whose page handler returns `void` or an underspecified payload
 * (waValidateStores / waTriggerQRTimeout / waTriggerLogout); the
 * helpers walk the field with `in` + `typeof`/`Array.isArray` checks so
 * no blind cast is needed.
 */
const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const pickString = (v: unknown, key: string): string | undefined => {
    if (!isObject(v)) return undefined;
    const x = v[key];
    return typeof x === 'string' ? x : undefined;
};
const pickBoolean = (v: unknown, key: string): boolean | undefined => {
    if (!isObject(v)) return undefined;
    const x = v[key];
    return typeof x === 'boolean' ? x : undefined;
};
const pickStringArray = (v: unknown, key: string): string[] | undefined => {
    if (!isObject(v)) return undefined;
    const x = v[key];
    return Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : undefined;
};

import type {
    ValidateChatIdParams,
    GetMessageAckParams, GetMessageAckResult,
    GetWaMeInfoParams, GetWaMeInfoResult,
    IsWhatsAppConnectedParams, IsWhatsAppConnectedResult,
    GetChatObjectByChatIdParams, GetChatObjectByChatIdResult,
    GetChatParticipantsParams, GetChatParticipantsResult,
    GetCommonGroupsParams, GetCommonGroupsResult,
    CreateGroupParams, CreateGroupResult,
    UpdateGroupNameParams, UpdateGroupNameResult,
    GetGroupDataParams, GetGroupDataResult,
    UpdateGroupDescriptionParams, UpdateGroupDescriptionResult,
    AddGroupParticipantParams, AddGroupParticipantResult,
    RemoveGroupParticipantParams, RemoveGroupParticipantResult,
    SetGroupAdminRightsParams, SetGroupAdminRightsResult,
    RemoveGroupAdminRightsParams, RemoveGroupAdminRightsResult,
    SetGroupPictureParams, SetGroupPictureResult,
    LeaveGroupParams, LeaveGroupResult,
    QueryContactsAndChatsParams, QueryContactsAndChatsResult,
    GetLatestChatsAndMessagesParams,
    QueryMessagesParams, QueryMessagesResult,
    LoadMessagesFromPhoneParams, LoadMessagesFromPhoneResult,
    GetMessageMediaResult,
    GetMessageMediaUrlResult,
    GetMessagesAckDataParams, GetMessagesAckDataResult,
    GetMessageByKeyResult,
    ReactToMessageParams, ReactToMessageResult,
    PinMessageParams, PinMessageResult,
    UnpinMessageParams, UnpinMessageResult,
    CreateStatusParams, CreateStatusResult,
} from "@common/tools/whatsapp/wa-tools-server-actions";
import { AppThunkDispatch, CommonRootState } from "@common/types/common-root-state-type";
import { getStateDirectly, getStore } from "@common/state/state-provider";
import { MessageState } from "@common/types/interface";
import { ContentMessageServiceActions } from "@extension-context-content/services/contentMessageServiceActions";
import { withTimeout } from "@extension/wa-communication-layer/core/utils/wa-error-tracing";
import { ensureChatVisibleInFilteredList } from "@extension/wa-communication-layer/label/surface-incoming-chat";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Chat, GetProfilePicResponse, WhatsappChatMessage, WhatsappContact, WhatsappId } from "@bt-common/interfaces/whatsapp-types";
import moment from "moment";
import randomstring from 'randomstring';
import { RRule } from "rrule";
import { WaContact } from "@/common/models/waContact";
import { labelExpansionWaChatIds } from '@extension/context/content/components/shared/contact-bulk-select';

/**
 * Helper function to get the first available WhatsApp engine ID from connected sockets.
 * Used for server-mode operations that need to route requests through an engine.
 *
 * @param state - The CommonRootState containing ConnectedEnginesReducer
 * @returns The engine ID or undefined if no WhatsApp engine is connected
 */
const getFirstAvailableEngineId = (state: CommonRootState): string | undefined => selectOnlineEngines(state)[0]?.id;

/**
 * Helper function to get engine ID or throw if not available.
 * Consolidates the common pattern of getting and validating engine ID.
 *
 * @param state - The CommonRootState containing ConnectedEnginesReducer
 * @returns The engine ID (guaranteed to be defined)
 * @throws Error if no engine is connected
 */
const getRequiredEngineId = (state: CommonRootState): string => {
    const engineId = getFirstAvailableEngineId(state);
    if (!engineId) {
        throw new Error('No engine selected for server mode operation');
    }
    return engineId;
};

export class Deferred<T = any> {
    promise: Promise<T>;
    reject!: () => void;
    resolve!: (data: any) => void;

    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.reject = reject;
            this.resolve = resolve;
        });
    }
}


const analyticsService = new AnalyticsService();

// if (process.env.NODE_ENV === "development" && window.location.hostname.includes("localhost")) {
//     new MockWapi();
// }


export const setAppStateThunk = createAsyncThunk<
    void,
    string
>("setAppStateThunk", async (state, { dispatch }) => {
    console.log("setAppStateThunk", state);
    dispatch(setAppState(state));
});


// Select active bots
export const getActiveBotsSelector = createDeepEqualSelector(
    [
        (state) => state.DBBotReducer,
    ],
    (DBBotReducer) => {
        const bots = botSelectByQuery(DBBotReducer, {});
        return bots.filter(bot => bot.activeDeploymentId);
    }
);

// Select active bots whose trigger scope includes the given senderId.
// Uses the deployed flowJSON (runtime authority) and falls back to the
// draft flow on the bot if the deployment is not loaded in this context.
export const getActiveBotsRelevantForSenderSelector = createDeepEqualSelector(
    [
        (state) => state.DBBotReducer,
        (state) => state.DBBotDeploymentReducer,
        (_state, senderId: string) => senderId,
    ],
    (DBBotReducer, DBBotDeploymentReducer, senderId): Bot[] => {
        const activeBots = botSelectByQuery(DBBotReducer, {})
            .filter((bot: Bot) => !!bot.activeDeploymentId);
        return activeBots.filter((bot: Bot) => {
            const deployment = bot.activeDeploymentId
                ? botDeploymentSelectOneObjectByQuery(DBBotDeploymentReducer, { _id: bot.activeDeploymentId })
                : null;
            const flowJSON = deployment?.flowJSON ?? bot.flowJSON;
            return isBotRelevantForSender(flowJSON, senderId);
        });
    }
);



/**
 * Pull the canonical sender JID out of an incoming WA message regardless
 * of which "shape" the page-context handed us.
 *
 * Modern WhatsApp Web with the LID (linked-identity) rollout wraps the
 * sender as `message.from.id._serialized` ("89751998…@lid"); the legacy
 * shape was `message.from._serialized` ("972526…@c.us") and some entry
 * points still pass a bare string. Reading only the legacy field made
 * `senderId` come back undefined on LID accounts → the whole chatbot
 * dispatch was skipped at `handleNewIncomingWaMessageThunk:244`.
 *
 * Order of preference:
 *   1. `from._serialized`        — legacy single-level wrap.
 *   2. `from.id._serialized`     — LID-era wrap.
 *   3. `from.id` as a string     — some adapters drop the wrapper.
 *   4. `from` as a string        — minimum fallback.
 */
/**
 * The shape WAWeb actually attaches as a message's `from` (and to/author).
 * Historically the flat {@link WhatsappId} (`{ _serialized }`); on linked-identity
 * (LID) accounts it's a nested wrapper whose real id lives at `.id` / `.id._serialized`
 * (and the outer `_serialized` may be absent). Some entry points also hand back a
 * bare serialized string. Modeled explicitly so {@link extractSenderJid} can
 * reconcile every form WITHOUT `any` — which is what previously let LID senders slip
 * through `message.from._serialized` reads as `undefined`.
 */
type WhatsappMessageSenderRef =
    | string
    | (Partial<WhatsappId> & { id?: string | Partial<WhatsappId> });

const extractSenderJid = (
    message: { from?: WhatsappMessageSenderRef | null } | null | undefined,
): string | undefined => {
    const from = message?.from;
    if (!from) return undefined;
    if (typeof from === 'string') {
        return from.trim() || undefined;
    }
    // from is the object form (flat WhatsappId or LID wrapper) — narrowed.
    if (typeof from._serialized === 'string' && from._serialized.trim()) {
        return from._serialized.trim();
    }
    const innerId = from.id;
    if (typeof innerId === 'string') {
        return innerId.trim() || undefined;
    }
    if (innerId && typeof innerId._serialized === 'string' && innerId._serialized.trim()) {
        return innerId._serialized.trim();
    }
    return undefined;
};

// `resolveSenderJidEquivalents` (the @lid<->@c.us equivalence used below) now
// lives in the shared pure util `@common/utils/jid-equivalents` so the display
// selector (UserSelectors, whatsapp-scheduler#165) applies the SAME rule. See the
// import near the top of this file. Behaviour is byte-for-byte the same.

// ensureWebhooksFresh is now imported from WaEventDispatchThunk.ts

// In-flight "add chat to Inbox" creations, keyed `${labelId}:${waChatId}`. The
// redux entity only lands once the server responds, so without this a burst of
// messages in one chat fires several concurrent creates and the DB ends up with
// duplicate contacts-labels rows for the same pair.
const inboxLabelCreationsInFlight = new Set<string>();

export const handleNewIncomingWaMessageThunk = createAsyncThunk<
    void,
    { message: WhatsappChatMessage; contactDisplayName?: string },
    { state: CommonRootState }
>("handleNewIncomingWaMessageThunk", async ({ message, contactDisplayName }, thunkApi) => {
    const { getState } = thunkApi;
    const dispatch = thunkApi.dispatch;
    const state = getState() as CommonRootState;
    console.log("handleNewIncomingWaMessageThunk", message);

    // ── Message Vault: backup incoming message body on arrival ──────
    //
    // The vault is a continuous mirror of every observed incoming
    // message body. When WA later shows a "this message was deleted"
    // placeholder, the deleted-message-injection looks up the body
    // from this vault and offers a Restore chip.
    //
    // Why HERE: this thunk fires for every `new_message_received_webhook`,
    // which `handleMessageAddWebhook` (page side) emits for incoming
    // messages only (it filters out fromMe). Outgoing capture is
    // explicitly out of scope for this MVP — the user wants incoming
    // only.
    //
    // Fire-and-forget. `captureMessage` is internally try/catched and
    // idempotent on msgId, so repeats (e.g. duplicate deliveries) no-op.
    try {
        const anyMsg = message as any;
        // Robust id — WA's id._serialized is undefined on current builds, so
        // resolveWaMsgSerializedId falls back to String(id)/messageId. Using
        // the raw read here silently skipped capture (the gate below saw '').
        const msgId = resolveWaMsgSerializedId(anyMsg);
        const body = String(anyMsg?.body ?? anyMsg?.caption ?? '');
        const msgType = String(anyMsg?.type ?? 'chat');
        // Voice notes ('ptt' = push-to-talk; 'audio' = attached audio
        // file) have NO text body — the audio itself is the recoverable
        // artifact, fetched asynchronously below.
        const isVoiceNote = msgType === 'ptt' || msgType === 'audio';
        // Images/videos: `body` usually holds the thumbnail base64, but WA
        // populates it ASYNCHRONOUSLY — a picture deleted seconds after
        // arrival fires this webhook before `body` is filled. Detect
        // whether `body` already looks like a base64 image; if not, fetch
        // the thumbnail on demand so fast-deleted images stay recoverable.
        const isImageOrVideo = msgType === 'image' || msgType === 'video';
        const bodyLooksLikeImage = /^(\/9j\/|iVBOR|UklGR|R0lGOD)/.test(body);
        const needsThumbFetch = isImageOrVideo && !bodyLooksLikeImage;
        // Gate ALL capture on the user's vault master switch
        // (resolveBackupConfig(...).vaultEnabled). Off → nothing captures.
        const built = isVaultCaptureEnabled(state) && msgId && (body.length > 0 || isVoiceNote || needsThumbFetch)
            ? buildVaultRowFromWaMessage(anyMsg)
            : null;
        if (built) {
            // Shared row builder; force fromMe:false — this webhook fires
            // for incoming messages only. Outgoing capture lives in the
            // own-message branch of handleWaEvent.
            const baseRow = { ...built, fromMe: false };
            if (isVoiceNote) {
                // Fetch the audio bytes FIRST, then capture the row once
                // with the audio attached — captureMessage is first-write-
                // wins on msgId, so capturing early and patching later
                // would lose the audio. Bytes arrive at message time (the
                // media is fresh on WA's CDN), and the whole thing is
                // fire-and-forget: a failed fetch just means this voice
                // note isn't recoverable, same as before this feature.
                void (async () => {
                    const audio = await fetchIncomingAudioForVault(String(msgId));
                    if (!audio && body.length === 0) {
                        console.log('[bt-vault] voice-note capture SKIP — audio fetch failed', msgId);
                        return;
                    }
                    void vaultCaptureMessage({ ...baseRow, ...(audio ?? {}) });
                })();
            } else if (needsThumbFetch) {
                // Image/video whose thumbnail wasn't in `body` yet. Fetch
                // it, then capture with the thumbnail as `body` (the shape
                // the recovery modal renders). If the fetch fails but we
                // had a caption, still capture the caption-only row.
                void (async () => {
                    const thumb = await fetchIncomingImageThumbForVault(String(msgId));
                    if (!thumb && body.length === 0) {
                        console.log('[bt-vault] image capture SKIP — thumb fetch failed and no caption', msgId);
                        return;
                    }
                    void vaultCaptureMessage({ ...baseRow, ...(thumb ?? {}) });
                })();
            } else {
                void vaultCaptureMessage(baseRow);
            }
        }
    } catch (err) {
        console.warn('[bt-vault] capture from incoming thunk failed:', err);
    }

    const user = selectUser(state);
    if (!user) {
        return;
    }

    let matchingBotIds: string[] = [];
    let botDispatch:
        | { overriddenFrom: any; resolvedSenderId: string; originalSenderId: string }
        | undefined;

    const senderId = extractSenderJid(message);
    const isBroadcast = chatKindOf(senderId) === 'status';
    const isNewsletter = chatKindOf(senderId) === 'channel';

    if (!senderId || isBroadcast || isNewsletter) {
        // Surface the shape we got so future regressions land in one
        // search — don't dump the whole message object, just the
        // identifying fields. `from` is typed as `WhatsappId` (which
        // doesn't declare `.id`), but at runtime the LID shape DOES
        // carry a nested `.id._serialized` — cast through `any` for
        // the diagnostic read so we don't have to widen the public
        // type just for a log statement.
        const fromAny = message?.from as any;
        console.log("Skipping chatbot event: unsupported sender", {
            senderId,
            shape: {
                fromType: typeof fromAny,
                hasFromSerialized: typeof fromAny?._serialized === 'string',
                hasFromIdSerialized: typeof fromAny?.id?._serialized === 'string',
                fromIdType: typeof fromAny?.id,
            },
        });
    } else {
        // Build the set of JIDs the saved bot configs might key on. For a
        // @lid sender this includes the phone-form @c.us; for a @c.us
        // sender it includes the @lid form (in case the bot was set up
        // from a chat that WAWeb stored under @lid).
        let mergedContactMap = selectMergedContactMap(getState() as CommonRootState);
        let senderEquivalents = resolveSenderJidEquivalents(senderId, mergedContactMap);

        // Cache miss on first message from a not-yet-loaded contact: ask
        // WAWeb directly for the chat object. Its `.contact.phoneNumber`
        // carries the @c.us form. Upsert into the cache so the next
        // message hits the fast path.
        const onlyHaveLid =
            senderEquivalents.length === 1 &&
            senderId.endsWith('@lid');
        console.log('[chatbot-dispatch] lid-resolution gate', {
            senderId,
            onlyHaveLid,
            equivalentsBefore: senderEquivalents,
            mapHit: !!mergedContactMap?.[senderId],
        });
        if (onlyHaveLid && !mergedContactMap?.[senderId]) {
            try {
                console.log('[chatbot-dispatch] resolving @lid via chat-object tool', senderId);
                const chatRes = await waGetChatObjectByChatIdTool({ chatId: senderId });
                console.log('[chatbot-dispatch] chat-object raw result', {
                    senderId,
                    success: (chatRes as any)?.success,
                    hasData: !!(chatRes as any)?.data,
                    dataKeys: (chatRes as any)?.data
                        ? Object.keys((chatRes as any).data)
                        : [],
                });
                const chatData: any = (chatRes as any)?.success ? (chatRes as any).data : null;
                // The chat row's `chatId` is wire form: the resolved @c.us when
                // WAWeb knows the lid's phone number (a miss stays @lid).
                const phoneJid: string | undefined =
                    typeof chatData?.chatId === 'string' && chatData.chatId.endsWith('@c.us') ? chatData.chatId : undefined;
                console.log('[chatbot-dispatch] phoneJid search', {
                    senderId,
                    found: phoneJid,
                    contactKeys: chatData?.contact
                        ? Object.keys(chatData.contact)
                        : null,
                });
                if (phoneJid) {
                    dispatch(WaContactsCacheReducerActions.upsertOne({
                        _id: senderId,
                        _serialized: senderId,
                        id: { _serialized: senderId },
                        name: chatData?.contact?.name || chatData?.name,
                        phoneNumber: { _serialized: phoneJid },
                    } as any));
                    mergedContactMap = selectMergedContactMap(getState() as CommonRootState);
                    senderEquivalents = resolveSenderJidEquivalents(senderId, mergedContactMap);
                    console.log('[chatbot-dispatch] resolved @lid → @c.us', { senderId, phoneJid, senderEquivalents });
                } else {
                    console.warn('[chatbot-dispatch] chat object had no phoneNumber field', {
                        senderId,
                        contactSnapshot: chatData?.contact,
                    });
                }
            } catch (err) {
                console.warn('[chatbot-dispatch] chat-object lookup threw', { senderId, err });
            }
        }

        const relevantBots: Bot[] = senderEquivalents
            .flatMap((jid) => getActiveBotsRelevantForSenderSelector(state, jid))
            // dedupe by _id in case both forms matched the same bot
            .filter((b, i, arr) => arr.findIndex((x) => String(x._id) === String(b._id)) === i);

        console.log('[chatbot-dispatch] senderId resolution', {
            senderId,
            senderEquivalents,
            matchedBots: relevantBots.length,
        });

        if (relevantBots.length > 0) {
            // Pick the @c.us form as the "canonical" id we hand to the
            // backend, falling back to the original. The backend's
            // trigger evaluator also compares as a plain string, so
            // matching the form the user configured the trigger with is
            // what makes it fire there too.
            const canonicalSenderId =
                senderEquivalents.find((j) => j.endsWith('@c.us')) || senderId;
            // Backend's `resolveChatbotSender` reads
            //   chatbotData.from?.id?._serialized
            // (handle-chatbot-event.ts:115) — the NESTED `id._serialized`,
            // not the outer `from._serialized`. The LID-era WAWeb shape
            // wraps the JID one level deeper, and the backend already
            // unwraps that path. So we must override BOTH:
            //   - `from._serialized`     (legacy shape consumers)
            //   - `from.id._serialized`  (current shape, what the
            //                             chatbot trigger evaluator
            //                             actually reads)
            // Without the inner override, the server sees the original
            // @lid and trigger-node.ts:330 `contacts.includes(sender)`
            // returns false against a saved @c.us contact list.
            const overriddenFrom: any = {
                ...((message as any).from ?? {}),
                _serialized: canonicalSenderId,
                id: {
                    ...((message as any).from?.id ?? {}),
                    _serialized: canonicalSenderId,
                },
            };
            matchingBotIds = relevantBots.map((b) => String(b._id));
            botDispatch = {
                overriddenFrom,
                resolvedSenderId: canonicalSenderId,
                originalSenderId: senderId,
            };
        } else {
            console.log("No active bots relevant for sender", {
                senderId,
                senderEquivalents,
                hint: mergedContactMap?.[senderId]
                    ? 'contact found but no bot allow-lists matched any of its JID forms'
                    : 'contact not yet in mergedContactMap — try after the contact list has loaded',
            });
        }
    }

    // ── Unified dispatch: ONE request replaces the former three
    // (wa-message-listener-event + sendWebhookEvent + chatbot event). The
    // thunk gates on the shared relevance predicate and may send nothing.
    try {
        const consumers = await dispatch(dispatchWaEventThunk({
            type: 'receivedNewMessage',
            msg: message,
            contactDisplayName,
            matchingBotIds,
            botDispatch,
        })).unwrap();
        if (consumers?.webhooks && (message as any)?.fromMe !== true) {
            analyticsService.event("message_api_receive", {
                category: "messages",
                action: "api-receive",
                label: "regular",
            });
        }
    } catch (error) {
        console.error("Error dispatching unified wa event:", error);
    }

    // cancelIfReceived: cancel any scheduled message to this sender when they
    // message us first. Match the incoming sender against the scheduled
    // contactList ids using the SAME @lid ↔ @c.us reconciliation as the chatbot
    // path (resolveSenderJidEquivalents) — NOT a raw `message.from._serialized`
    // compare. On LID accounts the sender arrives as the @lid form while
    // contactList ids were saved by the chat picker as @c.us, so the old direct
    // `===` silently never matched and the message kept sending. We derive the
    // sender from extractSenderJid (handles the nested LID shape) and resolve its
    // equivalents off the merged contact map (already upserted by the chatbot
    // path's @lid resolution above when applicable).
    const cancelSenderEquivalents = new Set(
        senderId ? resolveSenderJidEquivalents(senderId, selectMergedContactMap(getState())) : []
    );
    const userMessageList = Object.keys(
        state.DBUserMessageReducer.entities
    ).map((id) => state.DBUserMessageReducer.entities[id]);
    const contactMessageList = userMessageList.filter((_message): _message is NonNullable<typeof _message> =>
        !!_message && (_message.contactList ?? []).some(
            (contact) => !!contact.id && cancelSenderEquivalents.has(contact.id)
        )
    );
    // Only a real reply cancels: a WhatsApp system event in the sender's chat
    // (security-code change, e2e notice, ciphertext, revoke, …) is not one.
    if (isCancelIfReceivedTrigger(message)) {
        for (const scheduledMessage of contactMessageList) {
            if (scheduledMessage.cancelIfReceived) {
                dispatch(deleteUserMessage(scheduledMessage._id));
            }
        }
    }

    // chatId is needed by the drip reply-exit block below (to detect group
    // inbounds) as well as by the inbox label logic further down, so it's
    // computed here, ahead of both.
    const chatId = message.from?._serialized || message.chatId?._serialized; // Fallback if from is missing

    // Drip reply-exit: report the sender's identity equivalents so the backend
    // exits this contact from any active stopOnReply drip, plus the group chat id
    // for a group inbound (a group audience's recipient is the @g.us id, which the
    // individual sender never matches).
    //
    // @lid reconciliation — THE stop-on-reply gap: WhatsApp addresses many contacts
    // by an opaque @lid, but audiences key recipients on the @c.us phone form.
    // `resolveSenderJidEquivalents` only bridges the two when the contact is already
    // in the merged contact map — a COLD @lid (first inbound, not yet cached) yields
    // ONLY the @lid, so the exit is recorded under a form the @c.us audience never
    // matches and the contact keeps receiving the sequence. Resolve it via WAWeb's
    // own lid→phone map (Contact.get / LidUtils.getPhoneNumber), which works WITHOUT
    // an open chat — unlike the chat-object lookup the chatbot path tries, which
    // returns success:false for a never-opened @lid. Detached async so the tool
    // round-trip never delays inbound handling (fire-and-forget, like before).
    void (async () => {
        const equivalents = new Set(cancelSenderEquivalents);
        if (senderId && senderId.endsWith('@lid') && ![...equivalents].some((j) => j.endsWith('@c.us'))) {
            try {
                const convResult = await triggerRequesthWhatsappTool(
                    WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED,
                    { waLidSerialized: senderId },
                );
                // triggerRequesthWhatsappTool wraps a handler payload lacking a
                // `data`/`error` key as `{success, data: <payload>}` — so the
                // handler's own `{success, convertedId}` lands at `.data.convertedId`.
                const cus = (convResult as any)?.data?.convertedId ?? (convResult as any)?.convertedId;
                if (typeof cus === 'string' && cus.endsWith('@c.us')) equivalents.add(cus);
            } catch (err) {
                console.warn('[drip-reply-exit] lid→c.us convert failed', senderId, err);
            }
        }
        // Only a real reply exits the drip: a WhatsApp system event in the
        // recipient's chat (security-code change, e2e notice, revoke, reaction…)
        // is not one — same gate as cancelIfReceived.
        const replyIds = isCancelIfReceivedTrigger(message) ? buildDripReplyIds(equivalents, chatId) : [];
        if (replyIds.length > 0) {
            // Best-effort snapshot of the triggering message so the campaign UI can
            // show WHAT the recipient replied. For media the body often holds a
            // base64 thumbnail (image/sticker) while the human text is the caption;
            // only carry a small thumbnail to keep the drip doc lean.
            const anyMsg = message as any;
            const rawBody = String(anyMsg?.body ?? '');
            const caption = String(anyMsg?.caption ?? '');
            const msgType = String(anyMsg?.type ?? 'chat');
            const MEDIA_TYPES = ['image', 'video', 'audio', 'ptt', 'document', 'sticker', 'gif'];
            const mediaType = MEDIA_TYPES.includes(msgType) ? msgType : undefined;
            const bodyLooksLikeImage = /^(\/9j\/|iVBOR|UklGR|R0lGOD)/.test(rawBody);
            const text = ((mediaType ? (caption || (bodyLooksLikeImage ? '' : rawBody)) : rawBody) || '').slice(0, 1000) || undefined;
            const thumbnail = ((mediaType === 'image' || mediaType === 'sticker') && bodyLooksLikeImage && rawBody.length < 60000)
                ? `data:image/jpeg;base64,${rawBody}`
                : undefined;
            const reply = (text || mediaType) ? { text, mediaType, thumbnail } : undefined;
            try {
                await dispatch(backendApi.endpoints.registerDripReply.initiate({ whatsappIds: replyIds, reply })).unwrap();
            } catch (err) {
                console.warn('[drip-reply-exit] registerDripReply failed', err);
            }
        }
    })();

    // --- INBOX LABEL LOGIC ---
    // If chat is NOT archived, add "inbox" label if not present
    const archivedIds = getArchivedContactIds(state);

    if (chatId && !archivedIds.includes(chatId)) {
        const inboxLabel = labelSelectOneObjectByQuery(state.DBLabelReducer, { name: 'inbox' });

        if (inboxLabel && user._id) {
            // `state` above was snapshotted at the top of this thunk, many awaits
            // ago. Re-read it here: two messages landing in the same chat seconds
            // apart both saw the stale "no inbox label yet" snapshot and each
            // created a row — which is how the DB grew up to 12 duplicate inbox
            // rows for a single chat (and why "Remove from Inbox" then needed one
            // click per duplicate).
            const freshState = getState() as CommonRootState;
            const hasInboxLabel = contactsLabelsSelectOneObjectByQuery(freshState.DBContactsLabelsReducer, {
                labelId: inboxLabel._id,
                waChatId: chatId
            });

            // Redux only learns about the new row once the server responds, so the
            // selector alone still leaves a window open for a burst of messages.
            // This in-flight guard closes it.
            const inFlightKey = `${inboxLabel._id}:${chatId}`;

            if (!hasInboxLabel && !inboxLabelCreationsInFlight.has(inFlightKey)) {
                inboxLabelCreationsInFlight.add(inFlightKey);
                try {
                    console.log('[handleNewIncomingWaMessageThunk] Adding inbox label to chat:', chatId);
                    await dispatch(DBContactsLabelsThunks.create({
                        waChatId: chatId,
                        labelId: inboxLabel._id,
                        labelName: inboxLabel.name,
                        owner: user._id
                    }));
                    const activeLabelId = freshState.LabelReducer.activeLabelId
                    if (activeLabelId === inboxLabel._id) {
                        // Awaited so the surfacing call below runs AFTER the re-filter
                        // has settled (the re-filter rebuilds _models; a stale allowList
                        // could otherwise re-drop the just-arrived chat — the inbox-tab race).
                        await dispatch(filterChatsByLabelIdThunk(activeLabelId))
                    }
                } catch (e) {
                    console.error('[handleNewIncomingWaMessageThunk] Failed to add inbox label:', e);
                } finally {
                    inboxLabelCreationsInFlight.delete(inFlightKey);
                }
            }
        }

        // FE#430 (stopgap): while a Blueticks label/tab is active,
        // `updateShouldAppearChatsByList` has reassigned `Store.Chat._models` to only
        // the chats matching that label, so a message arriving for a chat NOT in the
        // active label leaves it out of the rendered list — a brand-new conversation
        // never appears until a WA Web reload. Surface the incoming chat back into the
        // filtered view so it is never silently invisible, on ANY active tab (not just
        // Inbox). No-op when no filter is active; best-effort (never throws). The proper
        // fix (stop mutating `_models`; filter at the view layer) is the FE#430 follow-up.
        ensureChatVisibleInFilteredList(chatId);
    }
});

// Poll votes must ALSO drive the bot (not just the webhook bus): when the user
// taps a poll option, the bot should react as if they'd typed the choice. We
// forward the raw event to the webhook bus AND synthesize an inbound message
// (body = the chosen option name(s)) routed through the normal bot pipeline.
// This is what makes tap-to-answer polls actionable for the AI agent.
export const handlePollVoteWebhookThunk = createAsyncThunk<void, any, { state: CommonRootState }>(
    "handlePollVoteWebhookThunk",
    async (data, thunkApi) => {
        const { getState, dispatch } = thunkApi;
        if (!selectUser(getState() as CommonRootState)) return;
        // 1) Existing webhook fan-out.
        dispatch(dispatchWaEventThunk({ type: "poll_vote_webhook", msg: data }));

        // 2) Bot bridge — synthesize an inbound message from the latest vote.
        try {
            const vote = data?.latestVote ?? (Array.isArray(data?.votes) ? data.votes[0] : undefined);
            const chatId: string | undefined = data?.chatId;
            const voter: string | undefined = vote?.voter || chatId;
            const optionNames: string[] = Array.isArray(vote?.selectedOptionNames) ? vote.selectedOptionNames : [];
            const body = optionNames.join(', ').trim();
            if (!chatId || !voter || !body) return;

            // 2a) btgpt AI-conversation bridge: a vote in a LINKED group becomes a
            // linked-chat turn input. The body is self-describing ("[poll vote]
            // "<poll question>" → <option>") so the headless agent knows WHICH
            // task poll was answered and can update that item's status. Routed
            // via the aiLinkedChat relevance lane (fromMe-independent); the
            // backend's linked-chat seam handles it like any group message.
            // Linked groups have no classic bots, so skip the bot bridge below.
            if (isAiLinkedChat(chatId)) {
                const pollVoteMsg: any = {
                    id: {
                        _serialized: `${chatId}_false_pollvote_${Date.now()}`,
                        remote: { _serialized: chatId, server: chatId.split('@')[1] },
                        fromMe: false,
                    },
                    from: { _serialized: voter, id: { _serialized: voter } },
                    to: { _serialized: chatId },
                    body: `[poll vote] "${String(data?.pollName ?? '').trim()}" → ${body}`,
                    type: 'chat',
                    fromMe: false,
                    t: Math.floor(Date.now() / 1000),
                    aiLinkedChat: true,
                    __btPollVote: { pollName: data?.pollName, messageId: data?.messageId, options: optionNames },
                };
                console.log('[poll_vote_webhook] bridging vote to AI conversation', { chatId, body });
                dispatch(dispatchWaEventThunk({ type: 'receivedNewMessage', msg: pollVoteMsg }) as any);
                return;
            }

            // Test-prefix mode: let the account vote on its OWN poll to mimic a
            // user (single-account testing). A self-vote's `voter` is the
            // account itself, which wouldn't route to the contact's bot
            // session — so attribute the synthetic message to the chat PEER
            // (chatId) instead. In real 2-account use the voter IS the peer.
            let testMode = false;
            try { testMode = window.localStorage?.getItem('BT_TEST_PREFIX') === '1'; } catch { /* no localStorage */ }
            const fromJid = testMode ? chatId : voter;

            const syntheticMsg: any = {
                id: {
                    _serialized: `${chatId}_false_pollvote_${Date.now()}`,
                    remote: { _serialized: chatId, server: chatId.split('@')[1] },
                    fromMe: false,
                },
                from: { _serialized: fromJid, id: { _serialized: fromJid } },
                to: { _serialized: chatId },
                body,
                type: "chat",
                fromMe: false,
                t: Math.floor(Date.now() / 1000),
                // Provenance so downstream can tell this came from a poll tap.
                __btPollVote: { pollName: data?.pollName, messageId: data?.messageId, options: optionNames },
            };
            console.log("[poll_vote_webhook] bridging vote to bot", { chatId, voter, from: fromJid, body, testMode });
            await dispatch(handleNewIncomingWaMessageThunk({ message: syntheticMsg }));
        } catch (err) {
            console.warn("[poll_vote_webhook] bot bridge failed", err);
        }
    },
);
/** Public deletion thunk.
 *
 *  WhatsApp Web's revoked-message placeholder row in the DOM carries a
 *  data-id that's UNRELATED to the original message's id — so the
 *  injection layer can't suffix-match the placeholder against the
 *  vault. This thunk fires the moment WA emits the deletion event,
 *  with the ORIGINAL `messageId` still in the payload, BEFORE the new
 *  placeholder row is mounted.
 *
 *  We use that window to:
 *    1. Look up the original body in the vault by msgId
 *    2. Push it onto a short-lived "recent deletions" FIFO
 *    3. The injection consumes from that FIFO when it spots a
 *       placeholder row it can't match by id
 *
 *  After recording the deletion we forward the event (dispatchWaEventThunk)
 *  so user-configured webhooks (Zapier/Make/etc.) keep
 *  firing without behaviour change. */
export const handleMessageDeletedRevokedWebhookThunk = createAsyncThunk<
    void,
    any,
    {}
>("vaultRecordThenForwardDeletedRevoked", async (data, thunkApi) => {
    console.log('[bt-vault] deletion thunk INVOKED — messageId:', data?.messageId, 'chatId:', data?.chatId);
    const forward = () => thunkApi.dispatch(dispatchWaEventThunk({ type: 'message_deleted_revoked_webhook', msg: data }));
    try {
        const msgId = String(data?.messageId ?? '');
        const chatId = String(data?.chatId ?? '');
        // WA's payload reports the seconds-precision timestamp; normalise
        // to ms so we can compare with vault rows directly.
        const tsRaw = data?.whatsappTimestamp;
        const deletionTimestampMs =
            typeof tsRaw === 'number' && tsRaw > 0
                ? (tsRaw > 1e12 ? tsRaw : tsRaw * 1000)
                : 0;

        // TIER 1 — exact match on the deletion event's messageId.
        // Works only when WA's revoke event preserves the original
        // message's id (rare on @lid chats).
        let captured = msgId ? await vaultGetMessage(msgId) : null;
        if (captured) {
            console.log('[bt-vault] deletion TIER 1 match (exact msgId)', msgId);
            pushDeletionFromVaultRow(captured);
            forward();
            return;
        }

        // TIER 2 — body is in the webhook payload.
        // For `deletionReason === 'removed'` (delete-for-me), WA hasn't
        // stripped the body yet — use it directly.
        const inlineBody = String(data?.message?.body ?? data?.message?.caption ?? '');
        if (inlineBody.length > 0) {
            console.log('[bt-vault] deletion TIER 2 match (body in payload)', { msgId, len: inlineBody.length });
            pushRecentDeletion({
                msgId: msgId || `inline-${Date.now()}`,
                body: inlineBody,
                caption: String(data?.message?.caption ?? ''),
                hadMedia: Boolean(data?.message?.hasMedia),
                authorName: String(data?.fromDetails?.name ?? data?.deletedByName ?? ''),
                whatsappTimestamp: deletionTimestampMs || Date.now(),
                capturedAt: Date.now(),
            });
            forward();
            return;
        }

        // TIER 3 — match by `whatsappTimestamp`.
        // WA preserves the ORIGINAL message's `t` on the revoke event
        // (the placeholder keeps the slot in chronological order). So
        // the deletion's whatsappTimestamp ≈ the original message's
        // send time. Find the captured message in this chat whose
        // whatsappTimestamp is closest, within 2 minutes tolerance.
        //
        // This handles the case where the user has sent 5 messages
        // and deletes the 3rd one: the most-recent-in-chat (TIER 4)
        // would pick the 5th, but timestamp match picks the 3rd
        // correctly.
        if (chatId && deletionTimestampMs > 0) {
            captured = await vaultGetNearestByTs(chatId, deletionTimestampMs);
            if (captured) {
                console.log('[bt-vault] deletion TIER 3 match (closest timestamp in chat)', {
                    deletionTimestamp: new Date(deletionTimestampMs).toLocaleTimeString(),
                    matchedTimestamp: new Date(captured.whatsappTimestamp).toLocaleTimeString(),
                    deltaSeconds: Math.round(Math.abs(captured.whatsappTimestamp - deletionTimestampMs) / 1000),
                    matchedMsgId: captured.msgId,
                    body: captured.body.slice(0, 60),
                });
                pushDeletionFromVaultRow(captured);
                forward();
                return;
            }
        }

        // TIER 4 — last-resort: most recent in chat. Used only when
        // the deletion event has no usable timestamp. May pick the
        // wrong message if multiple recent captures exist.
        if (chatId) {
            captured = await vaultGetMostRecentInChat(chatId);
            if (captured) {
                console.log('[bt-vault] deletion TIER 4 match (most recent in chat, fallback)', {
                    matchedMsgId: captured.msgId,
                    body: captured.body.slice(0, 60),
                });
                pushDeletionFromVaultRow(captured);
                forward();
                return;
            }
        }

        console.log('[bt-vault] deletion — no body available via any tier', { msgId, chatId });
    } catch (err) {
        console.warn('[bt-vault] deletion bridge failed:', err);
    }
    forward();
});

// Module-load marker so we can verify the new code is in the bundle.
console.log('[bt-vault] WhatsappThunks loaded — wrapped deletion thunk active');

/** Historical Vault Sync Thunk.
 *
 *  Fires once per session shortly after WA finishes its initial Store
 *  sync. The page side has already filtered + flattened messages into
 *  a plain object batch — this thunk just walks the batch and calls
 *  vaultCaptureMessage on each row.
 *
 *  Rationale: captureMessage's `&msgId` unique constraint means
 *  re-running this is harmless — already-captured messages no-op.
 *  So even if multiple WA tabs are open and all fire this, the final
 *  vault state converges. PHASE 7 (failure handling) — multiple tabs
 *  open: handled. */
export const handleVaultHistoricalSyncThunk = createAsyncThunk<
    void,
    any,
    {}
>("handleVaultHistoricalSyncThunk", async (data, thunkApi) => {
    // Gate on the vault master switch — the page sweep still emits, but
    // content decides whether to persist (design: vaultEnabled:false =
    // nothing captures).
    if (!isVaultCaptureEnabled(thunkApi.getState() as CommonRootState)) return;
    const batch: any[] = Array.isArray(data?.batch) ? data.batch : [];
    if (batch.length === 0) return;
    let captured = 0;
    let skipped = 0;
    for (const row of batch) {
        try {
            // Outgoing (fromMe) rows are now CAPTURED — the backup path
            // wants them. The vault-backup sweep passes includeFromMe.
            const ok = await vaultCaptureMessage({
                msgId: String(row?.msgId ?? ''),
                chatId: String(row?.chatId ?? ''),
                senderId: String(row?.senderId ?? ''),
                type: String(row?.type ?? 'chat'),
                body: String(row?.body ?? ''),
                caption: String(row?.caption ?? ''),
                hadMedia: Boolean(row?.hadMedia),
                authorName: String(row?.authorName ?? ''),
                fromMe: Boolean(row?.fromMe),
                whatsappTimestamp:
                    typeof row?.whatsappTimestamp === 'number' && row.whatsappTimestamp > 0
                        ? row.whatsappTimestamp
                        : Date.now(),
            });
            if (ok) captured++;
            else skipped++;
        } catch (err) {
            console.warn('[bt-vault] historical sync row failed:', err);
            skipped++;
        }
    }
    console.log(`[bt-vault] historical sync batch processed: ${captured} new, ${skipped} skipped (already-known, empty, or outgoing)`);
});
export const subscribeCurrentBoardToSocket = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("subscribeCurrentBoardToSocket", async (_, thunkApi) => {

    const { dispatch, getState } = thunkApi;
    const state = getState();

    const currentChat = state.WhatsAppReducer?.currentChat;
    if (!currentChat) return;
    const chatBoard = getBoardByChat(state, currentChat);
    if (chatBoard) {
        dispatch(notifierSubscribeThunk({ entityType: 'boards', entityId: chatBoard._id ?? '' }))
    }
});

export const setCurrentChatThunk = createAsyncThunk<
    void,
    { chat: Chat | null },
    { state: CommonRootState }
>("setCurrentChatThunk", async ({ chat }, thunkApi) => {

    const { dispatch, getState } = thunkApi;
    const state = getState()


    dispatch(privateSetCurrentChat(chat))

    if (!chat) return;
    const chatBoard = getBoardByChat(state, chat);
    if (chatBoard) {
        dispatch(notifierSubscribeThunk({ entityType: 'boards', entityId: chatBoard._id ?? '' }))
    }
});


export const calculateNextDuedate = (dueDate: moment.Moment, message: UserMessage) => {
    if (!!message.rruleset) {
        try {
            const rule: RRule = fetchRule(message.rruleset);

            // Create a new Date object for the current time in UTC
            let currentDateUTC = moment.utc();

            // Get max of current date in UTC and due date in UTC
            let maxDateUTC = dueDate.isAfter(currentDateUTC) ? dueDate : currentDateUTC;

            let nextUTC = rule.after(maxDateUTC.toDate());
            if (nextUTC) {
                // Convert UTC date back to moment object
                dueDate = moment.utc(nextUTC);
            } else {
                return null;
            }
        } catch (error) {
            console.error('Failed to parse rruleset in calculateNextDuedate:', message.rruleset, error);
            // Fall back to old recurrence logic if rruleset parsing fails
            const diffUnits = moment.utc().diff(dueDate, message.recurenceUnit, true);
            const recurrenceQuantity = message.recurrenceQuantity ?? 1;

            if (diffUnits > 0) {
                const periods = Math.floor(diffUnits / recurrenceQuantity);
                dueDate.add((periods + 1) * recurrenceQuantity, message.recurenceUnit);
            }
        }
    } else {
        const diffUnits = moment.utc().diff(dueDate, message.recurenceUnit, true);
        const recurrenceQuantity = message.recurrenceQuantity ?? 1;

        if (diffUnits > 0) {
            const periods = Math.floor(diffUnits / recurrenceQuantity);
            dueDate.add((periods + 1) * recurrenceQuantity, message.recurenceUnit);
        }
    }

    return dueDate;
}




// Narrow the untyped create-thunk payload to its board id without a cast.
const createdBoardId = (payload: unknown): string | undefined => {
    if (typeof payload !== 'object' || payload === null || !('_id' in payload)) return undefined;
    return typeof payload._id === 'string' ? payload._id : undefined;
};

export const createWAContactBoardThunk = createAsyncThunk<
    void,
    { clearInput?: boolean },
    { state: CommonRootState }
>("getCurrentChatThunk", async (options, thunkApi) => {
    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;
    const currentChatMaybe = state.WhatsAppReducer.currentChat

    const user = selectUser(state)

    if (!currentChatMaybe || !user) return;
    const currentChat: Chat = currentChatMaybe;

    // Resolve the current user's WA identity via the diagnostics tool —
    // state.WhatsAppReducer.me is not reliably populated across WA versions.
    let userWA: Partial<WhatsappContact> | undefined = state.WhatsAppReducer.me ?? undefined;
    if (!currentChat.isGroup && !userWA) {
        try {
            const meRes = await getWaMeInfoTool();
            if (meRes.success && meRes.data?.phone) {
                const phone = meRes.data.phone;
                const waId = `${phone}@c.us`;
                userWA = {
                    id: { _serialized: waId, user: phone, server: 'c.us' } as any,
                    displayName: meRes.data.name ?? phone,
                    isMyContact: true,
                    isWAContact: true,
                };
            } else if (!meRes.success) {
                console.warn('[createWAContactBoardThunk] getWaMeInfoTool error:', meRes.error);
            }
        } catch (error) {
            console.warn('[createWAContactBoardThunk] getWaMeInfoTool threw:', error);
        }
    }

    if (!currentChat.isGroup && !userWA) {
        console.warn('[createWAContactBoardThunk] aborted — could not resolve user WA identity');
        return;
    }

    function createParticipant(participant: Partial<WhatsappContact>) {
        return {
            waId: participant?.id?._serialized ?? "",
            name: participant?.displayName ?? "",
            isMyContact: participant?.isMyContact,
            isWAContact: participant?.isWAContact,
        };
    }

    // Participant list lives at `chat.groupMetadata.participants._models` —
    // the chat model itself has no `.participants` accessor. Collapse to
    // `participantContacts` (the WhatsappContact-shaped objects this thunk
    // actually consumes) so the rest of the code can stay agnostic.
    const participantContacts: Array<Partial<WhatsappContact>> = currentChat?.isGroup
        ? (currentChat.groupMetadata?.participants?._models ?? [])
            .map(p => p?.contact)
            .filter((c): c is WhatsappContact => !!c)
        : [];

    let personList = [];
    let _IdByWaChatIds: string[]

    if (chatKindOf(currentChat?.id?._serialized) === 'group') {
        personList = participantContacts.map(createParticipant);
        _IdByWaChatIds = [currentChat?.id?._serialized ?? ''].filter(Boolean)
    } else {
        if (currentChat.contact) personList.push(createParticipant(currentChat.contact));
        if (userWA) personList.push(createParticipant(userWA));
        // Avoid sending undefined/null values which break matching logic server/selector side
        _IdByWaChatIds = [currentChat?.id?._serialized, userWA?.id?._serialized].filter((s): s is string => !!s)
    }

    const board = {
        name: currentChat?.formattedTitle ?? currentChat?.displayName ?? currentChat?.name ?? currentChat.id?.user ?? 'New WA board',
        owner: user._id,
        personList: personList,
        isGroup: chatKindOf(currentChat?.id?._serialized) === 'group',
        isWABoard: true,
        _IdByWaChatIds: _IdByWaChatIds,
    };

    // #733: always clears the status afterwards; see with-new-wa-board-status.ts.
    const waBoard = await withNewWaBoardStatus(dispatch, () => dispatch(DBBoardsThunks.create(board)));

    const personProfilePicPromiseList = participantContacts.map(participant =>
        dispatch(getProfilePicThunk(participant.id?._serialized ?? '')).unwrap()
    )

    Promise.all(personProfilePicPromiseList).then((responseList) => {
        const personList: Person[] = responseList.filter((response: any) => !response.error)
            .map((response: GetProfilePicResponse) => {
                const participant: Partial<WhatsappContact> | undefined = participantContacts.find((participant) => participant?.id?._serialized === response.waId)
                return {
                    ...participant,
                    profilePic: response.profilePic,
                }
            })

        const boardId = createdBoardId(waBoard.payload);
        if (!boardId) return; // create failed or returned no board: nothing to update
        dispatch(updateBoardPersonListThunk({ boardId, personList }));
    })
});

export const toggleSelectAllContactsThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("toggleSelectAllContactsThunk", async (_, thunkApi) => {
    console.log("toggleSelectAllContactsThunk")

    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;


    const mergedContactList = selectMergedContactList(state);
    const _importedContactsArray = [...mergedContactList];

    if (state.WhatsAppReducer.importedContactsArray.length === 0) {
        const allContactList =
            dispatch(setImportedContactsArray([..._importedContactsArray]))
    } else {
        dispatch(setImportedContactsArray([]))
    }
})


export const handleContactSelectionChangedThunk = createAsyncThunk<
    void,
    { contact: ContactInfo; isSelected: boolean },
    { state: CommonRootState }
>("scheduleMessage", async ({ contact, isSelected }, thunkApi) => {
    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;
    const importedContactsArray = state.WhatsAppReducer.importedContactsArray;

    // Normalize contact ID - ensure whatsappId is set for consistent lookup/storage
    const contactId = contact.whatsappId || contact.id?._serialized;
    const normalizedContact: ContactInfo = {
        ...contact,
        whatsappId: contactId
    };

    const _importedContactsArray = [...importedContactsArray];
    const chatIndex = _importedContactsArray.findIndex(_contact => _contact.whatsappId === contactId);
    if (chatIndex > -1) {
        _importedContactsArray.splice(chatIndex, 1);
    }
    if (isSelected) {
        _importedContactsArray.push(normalizedContact);
    }
    dispatch(setImportedContactsArray(_importedContactsArray));
})

export const toggleContactSelectionThunk = createAsyncThunk<
    void,
    { contact: ContactInfo },
    { state: CommonRootState }
>("toggleContactSelectionThunk", async ({ contact }, thunkApi) => {
    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;

    // Normalize contact ID - ensure whatsappId is set for consistent lookup/storage
    const contactId = contact.whatsappId || contact.id?._serialized;
    const normalizedContact: ContactInfo = {
        ...contact,
        whatsappId: contactId
    };

    const isContactChecked = state.WhatsAppReducer.importedContactsArray.find(
        _contact => _contact.whatsappId === contactId
    ) !== undefined;

    dispatch(handleContactSelectionChangedThunk({ contact: normalizedContact, isSelected: !isContactChecked }));
});

/**
 * Bulk-add every contact carrying the given WhatsApp label to the current
 * recipient selection (#140 — "target everyone labelled X in one step").
 * Resolves the label's ContactLabels → waChatIds, hydrates a display name from
 * the wa-contacts cache when available, and applies an ADDITIVE selection change
 * (never deselects an already-picked contact). Returns the number added.
 */
export const selectContactsByLabelThunk = createAsyncThunk<
    number,
    { labelId: string },
    { state: CommonRootState }
>("selectContactsByLabelThunk", async ({ labelId }, thunkApi) => {
    const state = thunkApi.getState();
    const dispatch = thunkApi.dispatch;

    const contactLabels = contactsLabelsSelectByQuery(state.DBContactsLabelsReducer, {
        labelId,
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
    });

    // #294: a label does not sweep archived chats into a bulk add (pick them individually).
    const waChatIds = labelExpansionWaChatIds(contactLabels, (id) => waContactsCacheSelectors.selectById(state, id));
    let added = 0;
    for (const waChatId of waChatIds) {
        const cached = waContactsCacheSelectors.selectById(state, waChatId);
        const contact: ContactInfo = {
            whatsappId: waChatId,
            name: cached?.name,
            displayName: cached?.displayName,
        };
        await dispatch(handleContactSelectionChangedThunk({ contact, isSelected: true }));
        added++;
    }
    return added;
});




/**
 * Per-chat profile-pic emit. Matches the contract's
 * `WaGetProfilePicUrlResult` shape exactly: the canonical wire field is
 * `chatId`. (Historically this flip-flopped between `chatId` and
 * `contactId` — keep producers and consumers on `chatId` end-to-end so the
 * drift doesn't return.)
 */
export interface ProfilePicEntry {
    chatId: string;
    profilePic?: string;
}

export type GetProfilePicUrlResult = ProfilePicEntry[];

export async function waGetProfilePicUrlTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetProfilePicUrlTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetProfilePicUrlTool>>> {
    // Accept the legacy `contactIds` arg too while callers migrate to `chatIds`.
    const chatIds = (params?.chatIds ?? (params as any)?.contactIds ?? []).filter(Boolean);
    if (chatIds.length === 0) {
        return waToolError('invalid_input', 'chatIds is required');
    }

    const extractUrl = (raw: any): string | undefined => {
        if (!raw) return undefined;
        if (typeof raw === 'string') return raw;
        return raw.eurl || raw.img || raw.imgFull || raw.url;
    };

    const normalize = (raw: any): ProfilePicEntry[] => {
        const list = Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
        return list
            .map((item: any) => ({
                // Read the canonical `chatId`, falling back to the legacy
                // `contactId` emitted by pre-rename engines.
                chatId: String(item?.chatId ?? item?.contactId ?? ''),
                profilePic: extractUrl(item?.profilePic ?? item?.url),
            }))
            .filter((entry: ProfilePicEntry) => !!entry.chatId);
    };

    if (isWhatsappExtensionContext()) {
        const response: any = await WhatsappToolsClientActions.waGetProfilePicUrlTool(chatIds);
        return waToolSuccess(normalize(response));
    }

    // Server mode: use whatsappTools.getProfilePicUrl via MQTT. Dual-write the
    // legacy `contactIds` alias so a not-yet-rebuilt engine still resolves the
    // ids; drop it once the extension ships `chatIds`.
    const result: any = await triggerRequesthWhatsappTool(WA_TOOL_COMMANDS.waGetProfilePicUrlTool,
        { chatIds, contactIds: chatIds }
    );
    const normalized = normalize(result);
    return waToolSuccess(normalized);
}

/**
 * Wrapper around `waGetProfilePicUrlTool` that also caches each fetched
 * profile pic into `DBWaContactsCacheReducer` so selectors picking from
 * the contacts cache (e.g. `selectProfilePic`) see the new URLs.
 *
 * In server mode (outside WhatsApp extension), also patches the backend DB
 * (the user's wa-contacts-cache document) so the images are persisted and
 * don't need to be re-fetched next session.
 * In extension mode (inside WhatsApp), images come from WhatsApp directly
 * so we only update Redux locally without persisting to DB.
 *
 * Returns the same array as the underlying tool.
 */
export const fetchAndCacheProfilePicUrlsThunk = createAsyncThunk<
    ProfilePicEntry[],
    string | string[],
    { state: CommonRootState }
>('whatsapp/fetchAndCacheProfilePicUrls', async (chatIdOrIds, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    const isExtension = isExtensionContext();
    const chatIds = (Array.isArray(chatIdOrIds) ? chatIdOrIds : [chatIdOrIds]).filter(Boolean);
    const toolResponse = await waGetProfilePicUrlTool({ chatIds });
    // Normalize the (transition-tolerant) wire entries to the canonical
    // ProfilePicEntry shape, reading the legacy `contactId` alias when present.
    const results: ProfilePicEntry[] = (toolResponse.success ? toolResponse.data : [])
        .map((r) => ({ chatId: r.chatId ?? r.contactId ?? '', profilePic: r.profilePic }))
        .filter((e) => !!e.chatId);
    // #803: ids the engine did not answer (timed out page-side) are not "checked": release them so a
    // later render re-requests them instead of waiting out the 24h profilePicLastUpdate TTL.
    profilePicFetchRegistry.releaseUnanswered(chatIds, results.map((entry) => entry.chatId));
    if (!results.length) return results;

    const now = new Date().toISOString();
    const state = thunkApi.getState();

    // Collect entries for DB persistence.
    // All checked contacts get profilePicLastUpdate so the hook knows
    // not to re-fetch until the TTL expires (even if they have no pic).
    const dbUpdates: { chatId: string; profilePic: string }[] = [];

    results.forEach(({ chatId, profilePic }) => {
        if (!chatId) return;
        // Shape-agnostic O(1) lookup. The extension loads contacts live via
        // waQueryContactsAndChatsTool whose lean shape (post-#63/#64) has only
        // a top-level `chatId` — no nested `id._serialized` — so the previous
        // `{ 'id._serialized': chatId }` query never matched there and the
        // fetched pic URLs were silently dropped (default avatars everywhere
        // inside the extension).
        const existing = getWaContactBySerializedId(
            state.DBWaContactsCacheReducer?.entities,
            chatId
        );
        // Only annotate pic metadata onto a contact that ALREADY exists in the
        // cache. Creating a placeholder for an unknown contact inserts a
        // name-less entity; the find/search path uses addMany(), which SKIPS
        // ids that already exist, so when the full record (with `name`) later
        // arrives it is silently discarded — leaving the contact name-less and
        // therefore filtered out of name-search results (#110). The pic-check
        // timestamp is still persisted to the DB below regardless.
        if (existing) {
            const upsertData: any = {
                _id: existing._id,
                _serialized: chatId,
                id: { _serialized: chatId },
                profilePicLastUpdate: now,
            };
            if (profilePic) {
                upsertData.imgUrl = profilePic;
            }
            dispatch(WaContactsCacheReducerActions.upsertOne(upsertData));
        } else if (profilePic) {
            // No cache entity to annotate (and #110 forbids inserting a
            // name-less placeholder). Park the URL in
            // CalendarReducer.contactPicMap instead — ContactAvatar reads it
            // as its btProfilePic fallback — so surfaces whose contacts never
            // enter the cache (e.g. campaign View-logs rows on the website)
            // still render the avatar.
            dispatch(setContactPicMap([chatId, profilePic]));
        }

        // Persist both pic URLs and "no pic" timestamps to DB — the
        // wa-contacts-cache schema's $updateProfilePics action keys on
        // `chatId`; keep that field name at the DB boundary.
        dbUpdates.push({ chatId, profilePic: profilePic || '' });
    });

    // Server mode: batch-persist to the user's wa-contacts-cache document in DB
    if (!isExtension && dbUpdates.length > 0) {
        dispatch(patchEntity.initiate({
            service: 'wa-contacts-cache',
            entity: { _id: 'profile-pics', $updateProfilePics: dbUpdates },
        }));
    }

    return results;
});

const chatNameInflight = new Set<string>();

/**
 * Resolve a chat's display name via waGetChatObjectByChatIdTool and upsert it
 * into DBWaContactsCacheReducer. Once written, selectMergedContactMap (used
 * by notes / reminders / etc.) will surface the name automatically.
 *
 * Idempotent per chatId per session: in-flight requests are deduped, and the
 * thunk short-circuits when the cache already has a name for the chatId.
 */
export const fetchAndCacheChatNameThunk = createAsyncThunk<
    string | null,
    string,
    { state: CommonRootState }
>('whatsapp/fetchAndCacheChatName', async (chatId, thunkApi) => {
    if (!chatId) return null;
    if (chatNameInflight.has(chatId)) return null;

    const state = thunkApi.getState();
    const existing = getWaContactBySerializedId(
        state.DBWaContactsCacheReducer?.entities,
        chatId
    );
    if (existing?.name) return existing.name;

    chatNameInflight.add(chatId);
    try {
        const response = await waGetChatObjectByChatIdTool({ chatId });
        if (!response.success || !response.data) return null;

        const name = typeof response.data.name === 'string' ? response.data.name : null;
        if (!name) return null;

        const entityId = existing?._id || chatId;
        thunkApi.dispatch(WaContactsCacheReducerActions.upsertOne({
            _id: entityId,
            _serialized: chatId,
            id: { _serialized: chatId },
            name,
        } as any));

        return name;
    } finally {
        chatNameInflight.delete(chatId);
    }
});

/**
 * Clears the imgUrl from a contact in Redux (and DB in server mode).
 * Called when ContactAvatar detects an expired/broken image URL.
 * Once cleared, the lazy-load hook sees `!contact.imgUrl` and re-fetches.
 */
export const clearExpiredProfilePicThunk = createAsyncThunk<
    void,
    string,
    { state: CommonRootState }
>('whatsapp/clearExpiredProfilePic', async (contactId, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    const isExtension = isExtensionContext();
    const state = thunkApi.getState();

    const existing = getWaContactBySerializedId(
        state.DBWaContactsCacheReducer?.entities,
        contactId
    );
    const entityId = existing?._id || contactId;

    // Clear imgUrl from Redux so the list sees it as missing
    dispatch(WaContactsCacheReducerActions.updateOne({
        id: entityId,
        changes: { imgUrl: undefined, profilePicLastUpdate: undefined },
    }));

    // Also clear the contactPicMap fallback entry — an empty string is falsy
    // downstream, so ContactAvatar drops back to the default SVG and its
    // self-fetch effect is free to re-request a fresh URL.
    dispatch(setContactPicMap([contactId, '']));

    // Server mode: clear imgUrl in the user's wa-contacts-cache document
    if (!isExtension) {
        dispatch(patchEntity.initiate({
            service: 'wa-contacts-cache',
            entity: { _id: 'profile-pics', $updateProfilePics: [{ chatId: contactId, profilePic: null }] },
        }));
    }
});

export const getWaVersionThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("getWaVersionThunk", async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch;
    const state = thunkApi.getState();
    const user = selectUser(state)

    const versionResp = await postMessageWithResponse({ cmd: "getWaVersion" });
    const version: string = typeof versionResp === 'string' ? versionResp : '';

    if (version && user) {
        if (!user.waVersion || user.waVersion !== version) {
            dispatch(DBUserThunks.patch({
                entity: {
                    _id: user._id,
                    waVersion: version
                }
            }))
        }
    }
});


export const clearChatInputThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    'clearChatInputThunk', async (_, thunkApi) => {
        const { getState } = thunkApi;
        const currentChat = getState().WhatsAppReducer.currentChat;
        ContentMessageServiceActions.clearChatInput(currentChat?.id?._serialized);
    });



export const getMediaFromClipboardToSend = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    'media/sendMedia', async (_, thunkApi) => {
        const { getState, dispatch } = thunkApi;
        const state = getState();

        const user = selectUser(state);
        if (!user) {
            dispatch(accessFormPopupActions.setStatus('Login'));
            return;
        }

        try {
            const mediaObj = await ContentMessageServiceActions.getMedia(state.WhatsAppReducer.currentChat?.id?._serialized ?? "");
            const currentChat = state.WhatsAppReducer.currentChat;

            if (!currentChat) {
                throw new Error('No chat selected');
            }

            let image: File | undefined;
            let imageId: string | undefined;

            if (mediaObj?.mediaBlobList?.[0]) {
                const mediaDescriptor = mediaObj.mediaBlobList[0];
                const blobData = mediaDescriptor.file instanceof Blob || mediaDescriptor.file instanceof File
                    ? mediaDescriptor.file
                    : mediaDescriptor.file?.blob || mediaDescriptor.file?._blob;

                if (!blobData) {
                    throw new Error('Invalid media format');
                }

                const { name: normalizedName, type: normalizedType } = normalizeFileMetadata({
                    originalName: mediaDescriptor.filename,
                    mimeType: mediaDescriptor.mimetype,
                });

                const fileOptions: FilePropertyBag = normalizedType ? { type: normalizedType } : {};

                image = new File([blobData], normalizedName, fileOptions);
                imageId = randomstring.generate(10);
                blobMap.set(imageId, image);
            }

            const contact = {
                id: currentChat?.id?._serialized ?? '',
                name: currentChat?.formattedTitle ?? currentChat?.name ?? currentChat?.displayName ?? currentChat?.contact?.displayName ?? 'Unknown',
                formattedTitle: currentChat?.formattedTitle,
            };

            // DO NOT REMOVE THE setTimeout - WITHOUT IT SOMETIMES THE WHATSAPP IS STUCK
            setTimeout(() => {
                console.log("open editMessage with image")
                dispatch(addScheduledMessageButtonActions.editMessage({
                    message: mediaObj?.caption,
                    imageId,
                    contactList: [contact],
                    dueDate: moment().toDate(),
                    isRecurring: false,
                    state: MessageState.editing
                } as ExtendedUserMessage));
            }, 1000);
        } catch (error) {
            console.error('Failed to process media:', error);
            throw error;
        }
    }
);


export const waitForWhatsappInitializationThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("waitForConnectedThunk", async (_, thunkApi) => {
    const state = thunkApi.getState() as CommonRootState;

    const checkWhatsappInitialization = async () => {
        const isWhatsappInitialized = selectIsWhatsappInitialized(state);
        if (isWhatsappInitialized) {
            return true;
        }
        return false;
    };

    while (true) {
        const isInitialized = await checkWhatsappInitialization();
        if (isInitialized) {
            return;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
    }
});


export const forceRefreshContactsThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("forceRefreshContactsThunk", async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch;

    try {
        console.log('Starting force refresh of contacts...');
        await dispatch(DBWaContactsCacheThunks.find({ $forceContactsUpdate: true })).unwrap();
        console.log('Force refresh contacts completed successfully');
    } catch (error) {
        console.error('Force refresh contacts failed:', error);
        throw error; // Re-throw to allow the calling component to handle the error
    }
});

/**
 * Load all contacts and chats from WhatsApp and store in Redux
 * EXTENSION MODE ONLY - This thunk only runs in extension context
 * - Uses waQueryContactsAndChatsTool to fetch from WhatsApp directly
 * - Merges contacts and chats into a single list
 * - Sorting: chats first (WhatsApp order), then contacts, number-only entries last
 * - Stores in DBWaContactsCacheReducer for selectors to access
 */
export const loadAllContactsAndChatsInExtensionThunk = createAsyncThunk<
    { totalContacts: number; totalChats: number; mergedCount: number },
    void,
    { state: CommonRootState }
>("loadAllContactsAndChatsInExtensionThunk", async (_, thunkApi) => {
    const dispatch = thunkApi.dispatch;

    if (!isExtensionContext()) {
        return { totalContacts: 0, totalChats: 0, mergedCount: 0 };
    }

    try {
        const result = await waQueryContactsAndChatsTool({
            limit: -1,
        });

        const resultAny = result as any;
        const contacts = (resultAny.success ? resultAny.data?.contacts : undefined) || [];
        const chats = (resultAny.success ? resultAny.data?.chats : undefined) || [];

        // FE#434 — project chat rows to cache entities with a chatId-first entity key.
        // Post wire-contract 3.8 (ext 5.9.34+) `data.chats` are LEAN rows whose id
        // lives ONLY on `chatId` (createLeanChat — no `id._serialized`/`_serialized`).
        // The old inline `chat._id || chat.id?._serialized || chat._serialized || chat.id`
        // derivation resolved to `undefined` for every lean chat, so the merge below
        // (`if (item._id …)`) DROPPED them. GROUPS reach the cache ONLY via this chats
        // array (getContacts is @c.us persons-only), so every group vanished from the
        // Groups tab (newsletters were separately rescued by #192; groups were the
        // remaining casualty). Extracted to chatsToContactCacheEntities so the mapping
        // is unit-tested; it restores the intended "chats first to preserve their data"
        // merge for groups + DMs alike.
        const chatsAsContacts = chatsToContactCacheEntities(chats);

        // Transform contacts to have _id for Redux entity adapter
        // Note: minimal format has chatId at top level, extended format has id._serialized
        const contactsWithIds = contacts.map((contact: any, index: number) => ({
            ...contact,
            _id: contact._id || contact.id?._serialized || contact.chatId || contact.id,
            _isChat: false,
            _originalIndex: index,
        }));

        // #192: Newsletters (@newsletter channels) live only in `chats` (Store.Contact
        // has no newsletters) as lean rows keyed on `chatId`. Kept as a defensive
        // backstop now that chatsAsContacts (above) resolves `chatId` too — it
        // guarantees the channel entity shape even if a newsletter row ever lacks a
        // usable chatId. (NOTE: contacts are @c.us persons-only; GROUPS arrive via
        // `chats`, now rescued by the chatsAsContacts fix above — FE#434.)
        const newsletterEntities = newsletterChatsToCacheEntities(chats);

        // Merge and deduplicate by _id (chats first to preserve their data)
        const mergedMap = new Map<string, any>();
        [...chatsAsContacts, ...contactsWithIds, ...newsletterEntities].forEach((item) => {
            if (item._id && !mergedMap.has(item._id)) {
                mergedMap.set(item._id, item);
            }
        });

        const mergedContacts = Array.from(mergedMap.values());

        // Sort: chats with name → contacts with name → no-name entries
        // Within each group, preserve original WhatsApp order
        mergedContacts.sort((a, b) => {
            const groupA = getSortGroup(a);
            const groupB = getSortGroup(b);

            // Different groups: sort by group
            if (groupA !== groupB) return groupA - groupB;

            // Same group: preserve original WhatsApp order
            return (a._originalIndex || 0) - (b._originalIndex || 0);
        });

        if (mergedContacts.length > 0) {
            dispatch(WaContactsCacheReducerActions.upsertMany(mergedContacts));
            dispatch(WaContactsCacheReducerActions.setItemsLoadedLocaly(true));
            dispatch(WaContactsCacheReducerActions.setItemsLoaded(true));
        }

        return {
            totalContacts: contacts.length,
            totalChats: chats.length,
            mergedCount: mergedContacts.length,
        };
    } catch (error) {
        console.error('[loadAllContactsAndChats] FAILED:', error);
        throw error;
    }
});


export const waCheckAndRefreshContacts = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>("waCheckAndRefreshContacts", async (_, thunkApi) => {
    const state: CommonRootState = thunkApi.getState();
    const dispatch = thunkApi.dispatch;

    // Get all contacts from the reducer
    const contacts = state.DBWaContactsCacheReducer.entities;
    const contactsArray = Object.values(contacts) as WaContact[];
    const CONTACTS_REFRESH_INTERVAL = 60;

    // Check if reducer is empty or data is older than refresh interval
    const shouldUpdate = contactsArray.length === 0 ||
        (contactsArray[0]?.updatedAt &&
            moment().diff(moment(contactsArray[0].updatedAt), 'minutes') >= CONTACTS_REFRESH_INTERVAL
        );

    if (shouldUpdate) {
        console.log('[waCheckAndRefreshContacts] Data needs refresh, refreshing contacts');
        await waQueryContactsAndChatsTool({});
    }
});

export async function waGetLinkPreviewTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetLinkPreviewTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetLinkPreviewTool>>> {
    // Contract accepts both `link` (canonical) and `url` (legacy alias) —
    // page-context handler is the engine seam that historically reads
    // `data.url`. Surface the chosen value as `link` for the client call.
    const link = String(params?.link ?? params?.url ?? '').trim();
    if (!link) {
        return waToolError('invalid_input', 'link is required');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waGetLinkPreviewTool(link);
        return unwrapEngineResult(result);
    }

    return triggerRequesthWhatsappTool<{ link: string }, WaToolResult<typeof WA_TOOL_COMMANDS.waGetLinkPreviewTool>>(WA_TOOL_COMMANDS.waGetLinkPreviewTool, { link });
}

export async function waValidatePhoneNumberOrChatIdTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool>>> {
    const { chatId, options } = params;
    const useForceUsync = options?.useForceUsync;

    if (!chatId) {
        return waToolError('invalid_input', 'Phone number or Chat ID is required');
    }

    const isPhoneNumber = !chatId.includes('@');
    let resolvedChatId = chatId;
    if (isPhoneNumber) {
        const cleanPhone = chatId.replace(/[^\d]/g, '');
        if (!cleanPhone) {
            return waToolError('invalid_input', 'Phone number contains no valid digits');
        }
        resolvedChatId = `${cleanPhone}@c.us`;
    } else if (chatId.endsWith('@c.us')) {
        // Mirror the page-context normalization: non-digits in the local part are
        // stripped before the store lookup, so a chat id like "972...828A@c.us"
        // is silently checked as "972...828@c.us". Apply it here so the response
        // can echo back the id that was actually validated.
        const digits = chatId.slice(0, -'@c.us'.length).replace(/\D/g, '');
        if (!digits) {
            return waToolError('invalid_input', 'Chat ID contains no valid digits');
        }
        resolvedChatId = `${digits}@c.us`;
    }

    // Contract (WaValidatePhoneNumberOrChatIdResultSchema): a negative answer is
    // DATA — `{valid:false, chatId:null, errorCode}` — not an error. Only an
    // engine failure is an error.
    const invalid = (errorCode?: 'invalid_id' | 'not_on_whatsapp' | 'not_found') =>
        waToolSuccess({ valid: false, chatId: null, errorCode: errorCode ?? 'not_on_whatsapp' });

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waValidatePhoneNumberOrChatIdTool(resolvedChatId, { useForceUsync });
        if (!result || isWireError(result)) {
            return unwrapEngineResult(result);
        }
        if (!result.valid) return invalid(result.errorCode);
        return waToolSuccess({ valid: true, chatId: resolvedChatId });
    }

    const result = await triggerRequesthWhatsappTool<ValidateChatIdParams, WaToolResult<typeof WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool>>(WA_TOOL_COMMANDS.waValidatePhoneNumberOrChatIdTool, { chatId: resolvedChatId, options: { useForceUsync } });
    if (result.success === false) return result;
    if (!result.data?.valid) return invalid(result.data?.errorCode);
    // Read the canonical `chatId`, falling back to the legacy `formatted_chat_id`
    // alias emitted by pre-rename engines.
    return waToolSuccess({ valid: true, chatId: result.data?.chatId ?? result.data?.formatted_chat_id ?? resolvedChatId });
}

/**
 * Validate a phone number against WhatsApp and, if valid, open the chat.
 * Returns `{ success: true }` on success so callers can close their UI.
 * On failure, dispatches `setInvalidWANumber` with the error message.
 */
export const openPhoneChatThunk = createAsyncThunk<
    { success: boolean; error?: string },
    { phoneNumber: string },
    { state: CommonRootState }
>(
    "whatsapp/openPhoneChat",
    async ({ phoneNumber }, thunkApi) => {
        const dispatch = thunkApi.dispatch;

        if (!phoneNumber) {
            console.warn('[openPhoneChatThunk] Cannot open chat: empty phone number');
            return { success: false, error: 'Empty phone number' };
        }

        // Detect if this is a chat ID (contains @) or a phone number
        const isChatId = phoneNumber.includes('@');
        const chatId = isChatId ? phoneNumber : phoneNumber.replace('+', '');
        console.log('[openPhoneChatThunk] Opening chat for:', chatId, isChatId ? '(chat ID)' : '(phone)');

        // PREEMPTIVE VALIDATION: Check if number/chat ID is registered on WhatsApp before opening chat
        try {
            const validationResult = await waValidatePhoneNumberOrChatIdTool({ chatId: phoneNumber, options: { useForceUsync: true } });

            if (validationResult.success === false || !validationResult.data?.valid) {
                const errorMsg = (validationResult.success === false && validationResult.error.message) || 'Not a valid WhatsApp number';
                console.warn('[openPhoneChatThunk] Validation failed:', errorMsg);
                dispatch(setInvalidWANumber(errorMsg));
                return { success: false, error: errorMsg };
            }

            console.log('[openPhoneChatThunk] Validation passed, opening chat');
        } catch (validationError) {
            console.error('[openPhoneChatThunk] Validation error:', validationError);
            dispatch(setInvalidWANumber('Unable to validate number. Please try again.'));
            return { success: false, error: 'Validation error' };
        }

        await waOpenChatTool({ chatId });

        // Close the dialog and clear any prior error after successfully opening the chat
        dispatch(setStartChatWithPhoneNumberOpen(false));
        dispatch(setInvalidWANumber(''));

        return { success: true };
    }
);

// ============================================================================
// Label Domain Tools
// ============================================================================

/**
 * Tool for updating which chats should appear in the WhatsApp chat list.
 * Used for label filtering functionality.
 *
 * EXTENSION-ONLY BY DESIGN: this tool mutates the user's local WhatsApp Web
 * sidebar (which chats the extension shows/hides for label filtering). That
 * state lives only in the user's browser tab — it is meaningless engine-side
 * (a gateway pod has no sidebar UI), so server mode deliberately returns a
 * clear EXTENSION_ONLY error rather than routing over MQTT. See the
 * "Extension-only tools" list in whatsapp-tools-architecture.md.
 */
export type UpdateShouldAppearChatsPayload =
    | { [key: string]: boolean }
    | null
    | { filterType: string; excludeIds: string[] };

export async function waUpdateShouldAppearChatsByListTool(
    chatsThatShouldAppearInList: WaToolArgs<typeof WA_TOOL_COMMANDS.waUpdateShouldAppearChatsByListTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waUpdateShouldAppearChatsByListTool>>> {
    if (isWhatsappExtensionContext()) {
        WhatsappToolsClientActions.waUpdateShouldAppearChatsByListTool(chatsThatShouldAppearInList);
        return waToolSuccess(undefined as void);
    } else {
        // Extension-only by design (see JSDoc above): mutates the local
        // WhatsApp sidebar, which does not exist engine-side. No MQTT routing.
        console.warn('[updateShouldAppearChatsByListTool] This tool is extension-only by design (no engine-side sidebar to mutate)');
        return waToolError('unsupported_command', 'waUpdateShouldAppearChatsByListTool is extension-only by design (mutates local WhatsApp sidebar state)');
    }
}

/** Max voice-note size the vault will persist (binary bytes, pre-base64).
 *  ~1.5MB ≈ 12+ minutes of WhatsApp opus — anything bigger is likely a
 *  forwarded music/audio file; skip rather than bloat IndexedDB. */
const MAX_VAULT_AUDIO_BYTES = 1_500_000;

/** Fetch a voice note's raw bytes, without transcribing it.
 *
 *  Uses the same page primitive the Transcribe button drives:
 *  `transcribeAudio: true` makes the page treat audio as extractable
 *  and ship the decrypted bytes up in `_bytesForExtraction`. Crucially
 *  we NEVER call the extraction step here — no offscreen round-trip,
 *  no Whisper, no model download. Just bytes.
 *
 *  Used by the message vault and by cloud transcription (#393).
 *  Returns null on any failure (message gone, media expired, over
 *  `maxBytes`) — the caller treats that as "not available". */
export async function fetchVoiceNoteAudio(
    messageKey: string,
    maxBytes: number,
    logTag: string,
): Promise<{ audioBase64: string; audioMimetype: string; durationSec?: number } | null> {
    try {
        const pageResp: unknown = await postMessageWithResponse(
            EXTENSION_INTERNAL_COMMANDS.btMediaPrimitive,
            { messageKey, mode: 'preview', transcribeAudio: true },
            // Audio bytes are downloaded from WA's CDN + decrypted on the
            // page side — comfortably slower than the 10s default on long
            // notes / slow links.
            { timeoutOverride: 60_000 },
        );
        if (isWireError(pageResp)) return null;
        const parsed = WaGetMessageMediaPageResultSchema.safeParse(pageResp);
        if (!parsed.success) return null;
        const data = parsed.data;
        if (data.kind !== 'preview' || !data._bytesForExtraction?.dataBase64) return null;
        const { dataBase64, mimetype } = data._bytesForExtraction;
        if (!mimetype?.startsWith('audio/')) return null;
        // base64 inflates by 4/3 — compare against the inflated cap.
        if (dataBase64.length > (maxBytes * 4) / 3) {
            console.log(`${logTag} voice note exceeds size cap — skipping`, {
                messageKey,
                base64Length: dataBase64.length,
            });
            return null;
        }
        // Normalise 'audio/ogg; codecs=opus' → 'audio/ogg' so the value
        // is safe to embed in a data: URL later.
        const audioMimetype = mimetype.split(';')[0].trim() || 'audio/ogg';
        // WAWeb stores the length as a string of seconds (e.g. "56").
        const durationSec = Number(data.metadata?.duration ?? NaN);
        return Number.isFinite(durationSec) && durationSec > 0
            ? { audioBase64: dataBase64, audioMimetype, durationSec }
            : { audioBase64: dataBase64, audioMimetype };
    } catch (e) {
        console.warn(`${logTag} voice-note byte fetch failed`, e);
        return null;
    }
}

/** An incoming voice note's raw bytes for the message vault (vault size cap). */
function fetchIncomingAudioForVault(
    messageKey: string,
): Promise<{ audioBase64: string; audioMimetype: string } | null> {
    return fetchVoiceNoteAudio(messageKey, MAX_VAULT_AUDIO_BYTES, '[bt-vault]');
}

/** Raw base64 (no data: prefix) of a media message's thumbnail, or null.
 *
 *  WAWeb usually populates `msg.body` with the thumbnail base64 for
 *  image/video messages — but ASYNCHRONOUSLY. A picture received and
 *  deleted within a couple of seconds fires the incoming webhook before
 *  `body` is populated, so the vault sees an empty body and never backs
 *  it up (exactly the "deleted image, no Restore button" gap). This
 *  fetches the preview thumbnail on demand so those fast delete-after-
 *  send images are still recoverable.
 *
 *  We take the preview's `thumbnailDataUrl` (a `data:image/…;base64,…`
 *  URL) and strip the prefix down to raw base64 — the same shape WAWeb
 *  would have put in `body`, so the recovery modal's image detection
 *  renders it unchanged. Fire-and-forget: null on any failure. */
async function fetchIncomingImageThumbForVault(
    messageKey: string,
): Promise<{ body: string } | null> {
    try {
        const pageResp: unknown = await postMessageWithResponse(
            EXTENSION_INTERNAL_COMMANDS.btMediaPrimitive,
            { messageKey, mode: 'preview' },
            { timeoutOverride: 30_000 },
        );
        if (isWireError(pageResp)) return null;
        const parsed = WaGetMessageMediaPageResultSchema.safeParse(pageResp);
        if (!parsed.success) return null;
        const data = parsed.data;
        if (data.kind !== 'preview' || !data.thumbnailDataUrl) return null;
        // Strip the data-URL prefix → raw base64, matching WA's body shape.
        const comma = data.thumbnailDataUrl.indexOf(',');
        const b64 = comma >= 0 ? data.thumbnailDataUrl.slice(comma + 1) : '';
        if (!b64 || b64.length < 100) return null;
        return { body: b64 };
    } catch (e) {
        console.warn('[bt-vault] image-thumb fetch failed', e);
        return null;
    }
}

/**
 * "First recognition" of a received file (linked-AI-group flow + Identify
 * button). Two tiers — the browser-local CLIP model was REMOVED (measured
 * live: it answered "a screenshot" at 0.40 on a real scanned invoice):
 *
 *   1. FAST LOCAL (docs only, free, ~150ms): pdf.js text layer + bilingual
 *      keyword scan. Junk layers (MoneHash generator CSV) are gated out.
 *   2. SERVER API (images + scans + keyword misses): the `file-intel-identify`
 *      action — the SAME Claude (Sonnet) vision/text pipeline the agent's
 *      `files.identify` tool uses. Seconds, 90s-bounded server-side.
 *
 * Returns null for "worth retrying" outcomes (media not ready, network down) —
 * the auto-flow's retry loop re-invokes; a definitive server answer (even
 * 'other') is returned as-is.
 */
export async function classifyFileForTurn(
    messageKey: string | undefined,
    mimetype: string,
): Promise<{ kind: FileKind; confidence: number } | null> {
    // Stage-by-stage diagnostics (prefix [file-classify]) — every exit path
    // logs WHAT it saw so a failure is attributable to media-not-ready vs
    // no-text-layer(scan) vs server error.
    const t0 = Date.now();
    const log = (stage: string, extra?: Record<string, unknown>) =>
        console.log(`[file-classify] ${stage} (+${Date.now() - t0}ms)`, { messageKey, mimetype, ...(extra ?? {}) });
    if (!messageKey) { log('skip: no messageKey'); return null; }
    try {
        const isImage = /^image\//i.test(mimetype);
        const isPdf = mimetype === 'application/pdf';
        const isOfficeDoc = /officedocument|msword/i.test(mimetype);
        const isText = mimetype.startsWith('text/');
        const isDoc = isPdf || isOfficeDoc || isText;
        if (!isImage && !isDoc) { log('skip: unsupported mimetype'); return null; }
        log(`start route=${isImage ? 'image(server)' : 'doc(text-scan → server)'}`);

        // ── Tier 1: local text-layer keyword scan (docs only). ──
        if (isDoc) {
            const pageResp: unknown = await postMessageWithResponse(
                EXTENSION_INTERNAL_COMMANDS.btMediaPrimitive,
                { messageKey, mode: 'preview' },
                { timeoutOverride: 30_000 },
            );
            if (isWireError(pageResp)) { log('doc: media primitive wire error → retryable', { pageResp }); return null; }
            const parsed = WaGetMessageMediaPageResultSchema.safeParse(pageResp);
            if (!parsed.success || parsed.data.kind !== 'preview') {
                log('doc: media primitive bad shape → retryable', { zodOk: parsed.success });
                return null;
            }
            const b64 = parsed.data._bytesForExtraction?.dataBase64;
            const mt = parsed.data._bytesForExtraction?.mimetype || mimetype;
            if (!b64) {
                // Media still in WhatsApp's PREPARING/upload stage — the server
                // couldn't fetch it either. Retryable.
                log('doc: NO BYTES (media not ready) → retryable');
                return null;
            }
            const bin = atob(b64);
            const u8 = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
            log('doc: bytes ok → extracting text', { bytes: u8.length, extractMime: mt });
            const extracted = await extractPdfTextInContentScript(u8, mt, { maxChars: 4000 });
            const rawText = (extracted.text ?? '').trim();
            // Generator-metadata junk (MoneHash CSV) is NOT document content —
            // gate it so junk falls through to the server instead of producing
            // a false keyword verdict.
            const isJunk = looksLikeGeneratorMetadata(rawText);
            if (!isJunk) {
                const textResult = classifyDocTextToKind(rawText);
                if (textResult.kind !== 'unknown') {
                    log('doc: RESULT (local text keywords, free)', { textChars: rawText.length, ...textResult });
                    return textResult;
                }
            }
            log(
                isJunk
                    ? 'doc: text layer is GENERATOR JUNK → server identify (Claude)'
                    : rawText.length < 40
                        ? 'doc: little/no text layer (SCAN) → server identify (Claude)'
                        : 'doc: no keyword hit → server identify (Claude)',
                { textChars: rawText.length, textHead: rawText.slice(0, 80) },
            );
        }

        // ── Tier 2: server identify — Claude (Sonnet) vision/text via the
        // file-intel pipeline. The server fetches the media itself (MQTT
        // round-trip to this engine), rasterizes scans, and returns a full
        // FileIntelResult; we map doc_kind onto the coarse display FileKind.
        const fi = await postAction<FileIntelResult & { error?: string }>('file-intel-identify', { messageKey });
        if (fi?.error) {
            const notReady = /could not fetch media/i.test(String(fi.error));
            log(`server identify ERROR${notReady ? ' (media not ready) → retryable' : ''}`, { error: fi.error });
            return notReady ? null : { kind: 'unknown', confidence: 0 };
        }
        const kind = docKindToFileKind(fi?.doc_kind);
        const confidence = typeof fi?.confidence === 'number' ? fi.confidence : 0;
        log('server identify RESULT (Claude)', { doc_kind: fi?.doc_kind, summary: fi?.summary, kind, confidence });
        return { kind, confidence };
    } catch (e) {
        // Network/transport failure (backend down, fetch aborted) — retryable.
        console.warn(`[file-classify] FAILED with exception → retryable (+${Date.now() - t0}ms)`, { messageKey, mimetype }, e);
        return null;
    }
}

export async function waGetMessageMediaTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetMessageMediaTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessageMediaTool>>> {
    // Contract: `{waMessageKey: string, chatId?: string, mode?, transcribeAudio?}`.
    const waMessageKey = params?.waMessageKey;
    if (!waMessageKey) {
        return waToolError('invalid_input', 'waMessageKey is required');
    }
    // BE#366 — `agent-vision`: return a DOWNSIZED inline JPEG derivative (≤1024px, ~150–350KB) of an
    // image for Gemini vision. We fetch the FULL bytes from the page (mode 'full') and downsize them
    // locally, returning them INLINE (no GCS upload). It never touches the user's canonical media.
    const isAgentVision = params?.mode === 'agent-vision';
    const mode: 'preview' | 'full' = params?.mode === 'full' || isAgentVision ? 'full' : 'preview';
    const transcribeAudio = !!params?.transcribeAudio;

    // Non-extension (web app / server) — route over MQTT; the engine on the
    // other side runs this same thunk in extension context and returns the
    // already-projected wire shape. Pass `agent-vision` through so the remote
    // engine does the downsize on its side.
    if (!isWhatsappExtensionContext()) {
        return await triggerRequesthWhatsappTool<
            WaToolArgs<typeof WA_TOOL_COMMANDS.waGetMessageMediaTool>,
            WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessageMediaTool>
        >(
            WA_TOOL_COMMANDS.waGetMessageMediaTool,
            { waMessageKey, mode: isAgentVision ? 'agent-vision' : mode, transcribeAudio },
        );
    }

    // Extension context — content-script thunk OWNS orchestration. Calls the
    // page primitive (pure data extraction, no chrome.runtime needed) and
    // then handles upload (chrome.runtime-gated) + PDF extraction (pdfjs
    // worker URL via chrome.runtime) + BtSlim wire projection here.
    const pageResp: unknown = await postMessageWithResponse(
        EXTENSION_INTERNAL_COMMANDS.btMediaPrimitive,
        { messageKey: waMessageKey, mode, transcribeAudio },
        // Voice-note transcription ships the full audio bytes up from the
        // page — CDN download + decrypt can exceed the 10s default (the
        // exact failure users hit: "Timeout after 10000ms waiting for cmd
        // __bt:internal/mediaPrimitive"). Other modes keep the default.
        transcribeAudio ? { timeoutOverride: 60_000 } : undefined,
    );
    if (isWireError(pageResp)) {
        return unwrapEngineResult(pageResp);
    }
    const parsed = WaGetMessageMediaPageResultSchema.safeParse(pageResp);
    if (!parsed.success) {
        return waToolError(
            'engine_error',
            `Page primitive returned unexpected shape: ${parsed.error.message}`,
        );
    }
    const data = parsed.data;

    // Sentinel — message not found in store. Pass through so the MCP
    // boundary's detectEngineError catches it.
    if (data.kind === 'not_found') {
        return waToolSuccess(data);
    }

    // Preview branch — page ships raw bytes for PDF/text extraction inside
    // `_bytesForExtraction` (page-context lacks chrome.runtime for the
    // pdfjs worker URL). Run extraction here, then strip the internal-only
    // field before returning so it never reaches the LLM/wire.
    if (data.kind === 'preview') {
        if (data._bytesForExtraction?.dataBase64 && data._bytesForExtraction.mimetype) {
            try {
                const bin = atob(data._bytesForExtraction.dataBase64);
                const u8 = new Uint8Array(bin.length);
                for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                const extracted = await extractPdfTextInContentScript(u8, data._bytesForExtraction.mimetype);
                data.extractedText = extracted.text;
                if (extracted.language) data.language = extracted.language;
                // Structured transcription (audio only) — additive optional
                // wire field next to extractedText; carries flags/model/
                // timings for consumers that act on transcripts.
                if (extracted.transcription) data.transcription = extracted.transcription;
            } catch (e) {
                console.warn('[waGetMessageMediaTool] PDF extraction failed', e);
            }
        }
        const { _bytesForExtraction: _omit, ...preview } = data;
        void _omit;
        return waToolSuccess(preview);
    }

    // BE#366 agent-vision — downsize the image locally and return it INLINE (no GCS upload),
    // so the model gets a small transient derivative and the canonical media is untouched.
    // Non-image or resize failure falls through to the normal upload path below (safe).
    if (isAgentVision) {
        const mf = data.mediaFile as unknown as (Blob & { type?: string; name?: string }) | undefined;
        if (mf && typeof mf.type === 'string' && mf.type.startsWith('image/')) {
            try {
                const derived = await resizeImageForAgentVision(mf);
                if (derived) {
                    return waToolSuccess({
                        kind: 'full',
                        mediaFile: {
                            data: derived.dataBase64,
                            fileType: derived.mimeType,
                            fileName: mf.name,
                        },
                    } as WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessageMediaTool>);
                }
            } catch (e) {
                console.warn('[waGetMessageMediaTool] agent-vision resize failed; falling back to upload', e);
            }
        }
    }

    // Full branch — page emits raw `File` bytes. Upload to GCS via the
    // backend-proxied multipart endpoint, then project to the canonical
    // BtSlimMedia wire shape (#67).
    const uploaded = await uploadMediaFile(data.mediaFile);
    return waToolSuccess({
        kind: 'full',
        mediaFile: uploaded
            ? {
                mediaUrl: uploaded.mediaUrl,
                fileName: uploaded.fileName,
                fileType: uploaded.fileType,
                fileSize: uploaded.fileSize,
                expiresAt: uploaded.expiresAt,
                ...(typeof data.originalQuality === 'boolean' ? { originalQuality: data.originalQuality } : {}),
            }
            : null,
        // The page's classification when the download failed; an upload failure is 'error'.
        mediaUnavailable: uploaded ? null : (data.mediaUnavailable ?? 'error'),
    });
}

// `supportedUserId` is a diagnostics-only field (not part of the contract);
// kept as a separate parameter slot so DiagnosticsToolRunner can pass it
// through without bloating the contract args. Contract args are the
// canonical wire shape.
export async function waGetUnloggedWaMessageKeyIfExistInStoreTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE> & { supportedUserId?: string },
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE>>> {
    const chatId = String(params?.chatId || '').trim();
    const userMessageId = String(params?.userMessageId || '').trim();
    const dueDate = params?.dueDate instanceof Date ? params.dueDate : new Date(params?.dueDate ?? Date.now());

    if (!chatId) {
        return waToolError('invalid_input', 'Chat ID is required');
    }
    if (!userMessageId) {
        return waToolError('invalid_input', 'userMessageId is required');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.getUnloggedWaMessageKeyIfExistInStore(chatId, userMessageId, dueDate);
        return unwrapEngineResult(result);
    }

    return triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE>,
        WaToolResult<typeof WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE>
    >(WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE, {
        chatId,
        userMessageId,
        dueDate,
    });
}

export async function waGetMessagesAckDataTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetMessagesAckDataTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessagesAckDataTool>>> {
    if (!params?.messageKeys || !Array.isArray(params.messageKeys) || params.messageKeys.length === 0) {
        return waToolError('invalid_input', 'messageKeys array is required and must not be empty');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waGetMessagesAckDataTool(params.messageKeys);
        return unwrapEngineResult(result);
    }

    // Server-side dispatch — collapse the contract's `{waMessageKey?, messageId?}`
    // pairs to bare string keys (the wire-form). Backend-side handler accepts
    // the plural-typoed `waMessagesKeys` per the existing GetMessagesAckDataParams
    // shape; the contract `messageKeys` array is the typed inbound shape.
    // The legacy server-actions `{ackData: any[]}` result envelope is
    // projected into the contract's `{key, ack}[]` shape at the boundary.
    const waMessagesKeys = params.messageKeys
        .map((k) => k?.waMessageKey ?? k?.messageId ?? '')
        .filter((s): s is string => typeof s === 'string' && s.length > 0);
    const result = await triggerRequesthWhatsappTool<GetMessagesAckDataParams, GetMessagesAckDataResult>(
        WA_TOOL_COMMANDS.waGetMessagesAckDataTool,
        { waMessagesKeys },
    );
    if (result.success === false) return result;
    // The engine-side notifier returns the contract's flat {key, ack}[];
    // older engines wrapped it as { ackData: [...] }. Accept both.
    const ackData = Array.isArray(result.data)
        ? result.data
        : (Array.isArray(result.data?.ackData) ? result.data.ackData : []);
    const entries = ackData.map((entry: any) => ({
        key: typeof entry?.key === 'string' ? entry.key : (typeof entry?.waMessageKey === 'string' ? entry.waMessageKey : ''),
        ack: typeof entry?.ack === 'number' ? entry.ack : null,
    }));
    return waToolSuccess(entries);
}

/**
 * Get Message Ack Data - Returns acknowledgment data for a single message by key
 *
 * Dual-mode execution following the WhatsApp Tool-Based Architecture (Thunk Pattern):
 * - Extension Mode: Uses WhatsappToolsClientActions.getMessageAck()
 *   → postMessage → PageMessageService → waCommunicationLayerClient.getMessageAckDataByKey()
 *   → messageRetrievalOps.getMessageAckDataByKey()
 * - Server Mode: Uses dispatchWhatsappToolsGetMessageAck (lazy loaded to avoid circular dependency)
 *
 * Note: the legacy `validateAndNormalizeMessageKey` helper was removed —
 * thunks now consume `waMessageKey` directly per the contract.
 */
export async function waGetMessageAckTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetMessageAckTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessageAckTool>>> {
    const waMessageKey = params?.waMessageKey;
    if (!waMessageKey) {
        return waToolError('invalid_input', 'waMessageKey is required');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waGetMessageAckTool({ waMessageKey, chatId: params?.chatId });
        return unwrapEngineResult(result);
    }

    return await triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waGetMessageAckTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessageAckTool>
    >(WA_TOOL_COMMANDS.waGetMessageAckTool, { waMessageKey, chatId: params?.chatId });
}

/**
 * Get Poll Results - a poll's current votes: who voted for which option.
 *
 * Dual-mode, same shape as waGetMessageAckTool:
 * - Extension Mode: WhatsappToolsClientActions.waGetPollResultsTool()
 *   → postMessage → PageMessageService → getPollResults (poll-results.ts)
 * - Server Mode: triggerRequesthWhatsappTool
 */
export async function waGetPollResultsTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetPollResultsTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetPollResultsTool>>> {
    const waMessageKey = params?.waMessageKey;
    if (!waMessageKey) {
        return waToolError('invalid_input', 'waMessageKey is required');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waGetPollResultsTool({ waMessageKey, chatId: params?.chatId });
        // Not found is `null` — a nullable read's success (the backend maps it to 404);
        // unwrapEngineResult would turn it into "No response from engine" (a 502).
        return result === null ? { success: true, data: null } : unwrapEngineResult(result);
    }

    return await triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waGetPollResultsTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waGetPollResultsTool>
    >(WA_TOOL_COMMANDS.waGetPollResultsTool, { waMessageKey, chatId: params?.chatId });
}

// Contract args are `{}` (no fields); the diagnostics-only
// `supportedUserId` knob travels alongside the contract shape as a
// separate field so server-mode dispatch can forward it without
// bloating the canonical contract.
export async function getWaMeInfoTool(
    params?: WaToolArgs<typeof WA_TOOL_COMMANDS.getWaMeInfoTool> & { supportedUserId?: string },
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.getWaMeInfoTool>>> {
    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.getWaMeInfoTool();
        return unwrapEngineResult(result);
    }

    const supportedUserId = params?.supportedUserId;
    return await triggerRequesthWhatsappTool<GetWaMeInfoParams, GetWaMeInfoResult>(WA_TOOL_COMMANDS.getWaMeInfoTool, { supportedUserId });
}

// Returns the groups the current user shares with a contact ("Groups in
// common"). The diagnostics-only `supportedUserId` knob travels alongside
// the contract shape so server-mode dispatch can forward it.
export async function waGetCommonGroupsTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetCommonGroupsTool> & { supportedUserId?: string },
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetCommonGroupsTool>>> {
    const contactChatId = params?.contactChatId;
    console.log('[BT-COMMON-GROUPS][thunk] invoked. params=', params, 'isExtCtx=', isWhatsappExtensionContext());
    if (!contactChatId) {
        return waToolError('invalid_input', 'contactChatId is required');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waGetCommonGroupsTool({ contactChatId });
        console.log('[BT-COMMON-GROUPS][thunk] raw page result=', result, 'typeof=', typeof result);
        return unwrapEngineResult(result);
    }

    const queryParams: GetCommonGroupsParams & { supportedUserId?: string } = { contactChatId };
    if (params?.supportedUserId) queryParams.supportedUserId = params.supportedUserId;
    return await triggerRequesthWhatsappTool<GetCommonGroupsParams, GetCommonGroupsResult>(WA_TOOL_COMMANDS.waGetCommonGroupsTool, queryParams);
}

export async function isWhatsAppConnectedThunkTool(
    params?: WaToolArgs<typeof WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool> & { supportedUserId?: string },
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool>>> {
    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.isWhatsAppConnectedThunkTool();
        return waToolSuccess({
            connected: !!result?.connected,
            state: result?.state ?? undefined,
            stream: result?.stream ?? undefined,
            has_synced: result?.has_synced ?? undefined,
        });
    }

    const queryParams: Record<string, any> = {};
    if (params?.supportedUserId) queryParams.supportedUserId = params.supportedUserId;
    return triggerRequesthWhatsappTool<IsWhatsAppConnectedParams, IsWhatsAppConnectedResult>(WA_TOOL_COMMANDS.isWhatsAppConnectedThunkTool, queryParams);
}

export async function waValidateStoresTool(
    _?: WaToolArgs<typeof WA_TOOL_COMMANDS.waValidateStoresTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waValidateStoresTool>>> {
    if (isWhatsappExtensionContext()) {
        // Extension/engine context: validate stores via the page handler,
        // now registered under WA_TOOL_COMMANDS.waValidateStoresTool
        // (PageMessageService) rather than the old bare "validateStores"
        // string. Project the untyped wire payload into the canonical
        // optional shape via narrowing helpers (no blind cast).
        const result: unknown = await postMessageWithResponse(WA_TOOL_COMMANDS.waValidateStoresTool, {});
        if (isWireError(result)) {
            return unwrapEngineResult(result);
        }
        return waToolSuccess({
            success: pickBoolean(result, 'success'),
            missingModules: pickStringArray(result, 'missingModules'),
        });
    } else {
        // Server mode: route through backend -> MQTT -> engine. The engine
        // IS a WhatsApp page where the store modules exist, so validation is
        // meaningful there; the notifier proxies this same thunk, which then
        // runs the extension branch above on the engine side.
        return await triggerRequesthWhatsappTool<
            WaToolArgs<typeof WA_TOOL_COMMANDS.waValidateStoresTool>,
            WaToolResult<typeof WA_TOOL_COMMANDS.waValidateStoresTool>
        >(WA_TOOL_COMMANDS.waValidateStoresTool, {});
    }
}


export async function waTriggerQRTimeoutTool(
    _?: WaToolArgs<typeof WA_TOOL_COMMANDS.waTriggerQRTimeoutTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waTriggerQRTimeoutTool>>> {
    if (isWhatsappExtensionContext()) {
        // ClientAction returns Promise<void>; treat the resolved value as
        // the success ack for the contract's optional shape.
        const result: unknown = await WhatsappToolsClientActions.waTriggerQRTimeoutTool();
        if (result === undefined || result === null) {
            return waToolError('engine_error', 'No response from page context for triggerQRTimeout');
        }
        if (typeof result === 'boolean') {
            return waToolError('engine_error', 'Trigger QR timeout returned legacy boolean response (no verified action)');
        }
        if (pickBoolean(result, 'success') === false) {
            return waToolError('engine_error', pickString(result, 'error') || 'Trigger QR timeout failed');
        }
        return waToolSuccess({
            success: pickBoolean(result, 'success'),
            action: pickString(result, 'action'),
        });
    } else {
        const result = await triggerRequesthWhatsappTool<
            WaToolArgs<typeof WA_TOOL_COMMANDS.waTriggerQRTimeoutTool>,
            WaToolResult<typeof WA_TOOL_COMMANDS.waTriggerQRTimeoutTool>
        >(WA_TOOL_COMMANDS.waTriggerQRTimeoutTool, {});
        return result;
    }
}

export async function waTriggerLogoutTool(
    params?: WaToolArgs<typeof WA_TOOL_COMMANDS.waTriggerLogoutTool> & { supportedUserId?: string },
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waTriggerLogoutTool>>> {
    const supportedUserId = params?.supportedUserId;

    const connectionResult = await isWhatsAppConnectedThunkTool({ supportedUserId });
    if (connectionResult.success === false) {
        return waToolError(
            'engine_error',
            extractToolErrorMessage(connectionResult.error, 'Failed to verify WhatsApp connection state')
        );
    }

    if (!connectionResult.data?.connected) {
        return waToolError('not_connected', 'WhatsApp is not connected. Logout is not possible.');
    }

    if (isWhatsappExtensionContext()) {
        const unwrapped = unwrapEngineResult(await WhatsappToolsClientActions.waTriggerLogoutTool());
        if (!unwrapped.success) return unwrapped;
        return waToolSuccess({ success: pickBoolean(unwrapped.data, 'success') });
    } else {
        const result = await triggerRequesthWhatsappTool<
            WaToolArgs<typeof WA_TOOL_COMMANDS.waTriggerLogoutTool>,
            WaToolResult<typeof WA_TOOL_COMMANDS.waTriggerLogoutTool>
        >(WA_TOOL_COMMANDS.waTriggerLogoutTool, {});
        if (result.success === false) {
            return waToolError(
                'engine_error',
                extractToolErrorMessage(result.error, 'Trigger WA logout failed')
            );
        }
        return result;
    }
}

// Legacy `input` form (bare string or `{reason}`) kept for internal
// callers that pass a diagnostic reason. Contract args are `{}` — the
// reason field is not modeled in the canonical wire shape.
export async function waReloadPageTool(
    input: string | { reason?: string } = "",
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waReloadPageTool>> | unknown> {
    const reason = typeof input === 'string' ? input : (input?.reason || "");

    if (isExtensionContext()) {
        return withTimeout(ContentMessageServiceActions.waReloadPageTool(reason), "reloadPage", 50000);
    }

    return triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waReloadPageTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waReloadPageTool>
    >(WA_TOOL_COMMANDS.waReloadPageTool, { reason });
}

export async function waGetChatObjectByChatIdTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool>>> {
    // No `validateChatIdForTool` pre-flight here: the inner `getChatObjectByChatId`
    // already returns null when the chat isn't found (via WWebJSBt.getChat) and
    // throws "chatId is required" on empty input, so the extra round-trip to
    // waValidatePhoneNumberOrChatIdTool is pure overhead for this read-only lookup.
    if (!params.chatId || !String(params.chatId).trim()) {
        return waToolError('invalid_input', 'chatId is required');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.getChatObjectByChatId({ chatId: params.chatId });
        // Not found is `null` — a nullable read's success, not an error (A.9.1).
        return result === null ? { success: true, data: null } : unwrapEngineResult(result);
    }
    return triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool>
    >(WA_TOOL_COMMANDS.waGetChatObjectByChatIdTool, { chatId: params.chatId });
}

const collectSerializedIdVariants = (serializedId?: string): string[] => {
    if (!serializedId || typeof serializedId !== 'string') return [];

    const trimmed = serializedId.trim();
    if (!trimmed) return [];

    const withoutDeviceSuffix = trimmed.replace(/:\d+(?=@)/, '');
    const variants = new Set<string>([trimmed, withoutDeviceSuffix]);

    const addServerVariant = (value: string) => {
        if (value.endsWith('@lid')) {
            variants.add(`${value.split('@')[0]}@c.us`);
        } else if (value.endsWith('@c.us')) {
            variants.add(`${value.split('@')[0]}@lid`);
        }
    };

    addServerVariant(trimmed);
    addServerVariant(withoutDeviceSuffix);

    return Array.from(variants);
};

const getContactCandidateIds = (contact: any): string[] => {
    const rawIds = [
        contact?.whatsappId,
        contact?.id?._serialized,
        typeof contact?.id === 'string' ? contact.id : undefined,
        // BtSlimContact (engine wire shape) carries the JID under `chatId`;
        // local WaContact models still use `_serialized`. Accept both so this
        // shared helper keys either contact shape.
        contact?.chatId,
        contact?._serialized,
        contact?._id,
    ].filter((id): id is string => typeof id === 'string' && id.length > 0);

    const variants = new Set<string>();
    for (const id of rawIds) {
        for (const variant of collectSerializedIdVariants(id)) {
            variants.add(variant);
        }
    }

    return Array.from(variants);
};

const isLikelyPhoneLabel = (value?: string): boolean => {
    if (!value) return true;
    const cleaned = value.trim();
    if (!cleaned) return true;

    const withoutWaSuffix = cleaned.replace(/@(c\.us|g\.us|lid)$/i, '');
    if (/[^\d+\-().\s]/.test(withoutWaSuffix)) return false;

    return withoutWaSuffix.replace(/\D/g, '').length > 0;
};

const pickBestDisplayLabel = (candidates: Array<string | undefined>, fallback: string): string => {
    const nonEmptyCandidates = candidates
        .map((value) => typeof value === 'string' ? value.trim() : '')
        .filter(Boolean);

    const textCandidate = nonEmptyCandidates.find((value) => !isLikelyPhoneLabel(value));
    if (textCandidate) return textCandidate;

    return nonEmptyCandidates[0] || fallback;
};

const buildParticipantContactMap = (
    state: CommonRootState,
    additionalContacts: any[] = []
): Map<string, any> => {
    const contactMap = new Map<string, any>();
    const mergedContacts = selectMergedContactList(state) ?? [];
    const cacheContacts = Object.values(state?.DBWaContactsCacheReducer?.entities ?? {}).filter(Boolean) as any[];
    const allContacts = [...mergedContacts, ...cacheContacts, ...additionalContacts];

    for (const contact of allContacts) {
        const candidateIds = getContactCandidateIds(contact);
        if (candidateIds.length === 0) continue;

        for (const candidateId of candidateIds) {
            const current = contactMap.get(candidateId);
            if (!current) {
                contactMap.set(candidateId, contact);
                continue;
            }

            const currentLabel = getWaChatContactDisplayName(current, { fallback: '' });
            const incomingLabel = getWaChatContactDisplayName(contact, { fallback: '' });
            const shouldReplace =
                (!currentLabel && !!incomingLabel) ||
                (isLikelyPhoneLabel(currentLabel) && !!incomingLabel && !isLikelyPhoneLabel(incomingLabel));

            if (shouldReplace) {
                contactMap.set(candidateId, { ...current, ...contact });
            }
        }
    }

    return contactMap;
};

const normalizeChatParticipants = (rawResult: any, state: CommonRootState, additionalContacts: any[] = []): any[] => {
    const participants = Array.isArray(rawResult?.participants)
        ? rawResult.participants
        : Array.isArray(rawResult)
            ? rawResult
            : [];

    const mergedContactMap = buildParticipantContactMap(state, additionalContacts);

    return participants.map((participant: any) => {
        // BtSlimParticipant canonical: chatId is the phone-form @c.us JID.
        const serializedIdString = typeof participant?.chatId === 'string' ? participant.chatId : '';
        const candidateIds = collectSerializedIdVariants(serializedIdString);
        const mergedContact = candidateIds.map((candidateId) => mergedContactMap.get(candidateId)).find(Boolean);
        const mappedDisplayName = getWaChatContactDisplayName(mergedContact, { fallback: '' });
        const fallbackPhone = serializedIdString.split('@')[0] || '';
        const fallbackName = pickBestDisplayLabel([
            mappedDisplayName,
            mergedContact?.name,
            mergedContact?.displayName,
            mergedContact?.pushname,
            mergedContact?.shortName,
            participant?.displayName,
            participant?.name,
            participant?.pushname,
            participant?.shortName,
        ], fallbackPhone);
        const { displayName: _displayNameToRemove, ...participantWithoutDisplayName } = participant || {};

        return {
            ...participantWithoutDisplayName,
            name: fallbackName || "",
        };
    });
};

export async function waGetChatParticipantsTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetChatParticipantsTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetChatParticipantsTool>>> {
    // No `validateChatIdForTool` pre-flight here: the inner `getChatParticipants`
    // already throws "Chat not found" via WWebJSBt.getChat and "chatId is required"
    // on empty input, so the extra round-trip to waValidatePhoneNumberOrChatIdTool
    // is pure overhead for this read-only lookup.
    if (!params.chatId || !String(params.chatId).trim()) {
        return waToolError('invalid_input', 'chatId is required');
    }

    let state = getStateDirectly() as CommonRootState;
    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waGetChatParticipantsTool({
            chatId: params.chatId!,
            limit: params.limit,
            offset: params.offset,
            searchToken: params.searchToken,
        });
        // Surface page-context errors instead of normalizing them to an empty
        // participant page. The page handler validates chat-type there (e.g. a
        // newsletter has followers, not participants — see chat-operations
        // getChatParticipants) and throws; the seam delivers that as a wire
        // error whose `errorType` carries the machine-readable code
        // (`not_a_group`). Without this, every page throw silently became a
        // misleading "0 members" success.
        if (isWireError(result)) {
            return unwrapEngineResult(result);
        }
        let normalized = normalizeChatParticipants(result, state);

        // If all resolved labels are numbers, try refreshing contact caches
        // using the same flow as the contact list UI and map again.
        const shouldRefreshNames =
            normalized.length > 0 &&
            normalized.every((participant: any) => isLikelyPhoneLabel(participant?.name));

        if (shouldRefreshNames) {
            let additionalContacts: any[] = [];
            try {
                const refreshedContactsResult = await WhatsappToolsClientActions.waQueryContactsAndChatsTool({
                    includeChats: false,
                    includeContacts: true,
                    limit: -1,
                });
                const refreshedOk = refreshedContactsResult && !isWireError(refreshedContactsResult) ? refreshedContactsResult : undefined;
                additionalContacts = Array.isArray(refreshedOk?.contacts)
                    ? refreshedOk.contacts
                    : [];
                normalized = normalizeChatParticipants(result, state, additionalContacts);
            } catch (refreshViaQueryError) {
                console.warn('[waGetChatParticipantsTool] Failed to refresh participant names via queryContactsAndChats:', refreshViaQueryError);
            }

            const stillPhoneLabels =
                normalized.length > 0 &&
                normalized.every((participant: any) => isLikelyPhoneLabel(participant?.name));

            if (stillPhoneLabels) {
                try {
                    const dispatch = getStore().dispatch as AppThunkDispatch;
                    await dispatch(loadAllContactsAndChatsInExtensionThunk()).unwrap();
                    state = getStateDirectly() as CommonRootState;
                    normalized = normalizeChatParticipants(result, state, additionalContacts);
                } catch (refreshError) {
                    console.warn('[waGetChatParticipantsTool] Failed to refresh contacts for participant names:', refreshError);
                }
            }
        }

        // Paged envelope:
        // carry the engine-reported `total` (full member count, or the
        // name-matched count when searchToken is set) + echoed limit/skip so
        // the v1 service no longer has to guess `skip + page.length`. The
        // page handler owns the pagination + searchToken filtering.
        const rawData: any = result && !isWireError(result) ? result : undefined;
        return waToolSuccess({
            participants: normalized,
            total: typeof rawData?.total === 'number' ? rawData.total : normalized.length,
            limit: typeof rawData?.limit === 'number' ? rawData.limit : params.limit,
            skip: typeof rawData?.skip === 'number' ? rawData.skip : params.offset,
        });
    } else {
        // Server mode: Route through backend -> MQTT -> Engine -> WhatsApp
        // Dynamic import to avoid circular dependency
        const result = await triggerRequesthWhatsappTool<GetChatParticipantsParams, GetChatParticipantsResult>(WA_TOOL_COMMANDS.waGetChatParticipantsTool, {
            chatId: params.chatId!,
            limit: params.limit,
            offset: params.offset,
            searchToken: params.searchToken,
        });
        if (result.success === false) return result;
        const data: any = result.data;
        return waToolSuccess({
            participants: normalizeChatParticipants(data, state),
            total: typeof data?.total === 'number' ? data.total : undefined,
            limit: typeof data?.limit === 'number' ? data.limit : params.limit,
            skip: typeof data?.skip === 'number' ? data.skip : params.offset,
        });
    }
}

/**
 * Read WhatsApp's account-restriction ("reachout timelock") state and the new-chat send
 * quota that precedes it.
 *
 * Both engines answer this: the Chrome extension out of WA Web's user-prefs cache, the
 * Baileys pod over MEX — using the SAME GraphQL doc ids, so the reported shape matches.
 * That is what lets `session.restriction` mean one thing regardless of which engine wrote it.
 *
 * `status: 'unavailable'` means the probe could not run. Callers must not read that as
 * "no restriction": persisting a null on a failed probe silently clears a real one, and
 * the customer goes back to being told their sends failed for want of an engine.
 */
export async function waAccountRestrictionTool(
    params?: WaToolArgs<typeof WA_TOOL_COMMANDS.waAccountRestrictionTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waAccountRestrictionTool>>> {
    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waAccountRestrictionTool({ refresh: params?.refresh });
        return unwrapEngineResult(result);
    }
    const result = await triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waAccountRestrictionTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waAccountRestrictionTool>
    >(WA_TOOL_COMMANDS.waAccountRestrictionTool, { refresh: params?.refresh });
    return result;
}

/**
 * Would a send to this chat be refused because the account is restricted and the recipient
 * is not an established contact? Mirrors WhatsApp's own `canSendMsgWhileTimelocked`.
 *
 * Fails OPEN everywhere: an unreachable probe, an unknown chat, or a timeout all resolve to
 * `blocked: false`. The gate exists to stop an active restriction getting worse, so it must
 * never become a new reason for a healthy send to fail.
 */
export async function waChatSendRiskTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waChatSendRiskTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waChatSendRiskTool>>> {
    if (!params?.chatId || !String(params.chatId).trim()) {
        return waToolError('invalid_input', 'chatId is required');
    }
    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waChatSendRiskTool({ chatId: params.chatId });
        return unwrapEngineResult(result);
    }
    const result = await triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waChatSendRiskTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waChatSendRiskTool>
    >(WA_TOOL_COMMANDS.waChatSendRiskTool, { chatId: params.chatId });
    return result;
}

const normalizeGroupId = (value: any): string => String(value || '').trim();
const normalizeParticipantChatId = (value: any): string => String(value || '').trim();

const normalizeParticipantChatIds = (value: any): string[] => {
    if (Array.isArray(value)) {
        return value
            .map((item) => String(item || '').trim())
            .filter((item) => item.length > 0);
    }
    return String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
};

export async function waCreateGroupTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waCreateGroupTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waCreateGroupTool>>> {
    const groupName = String(params?.groupName || '').trim();
    const participantChatIds = normalizeParticipantChatIds(params?.participantChatIds);

    if (!groupName) {
        return waToolError('invalid_input', 'groupName is required');
    }
    if (participantChatIds.length === 0) {
        return waToolError('invalid_input', 'participantChatIds is required');
    }

    // Validate every participant chatId the same way the single-participant
    // group tools (add/remove/admin) do — waCreateGroupTool was the only
    // group tool missing this per-participant pre-flight.
    for (const participantChatId of participantChatIds) {
        const participantError = await validateChatIdForTool(participantChatId);
        if (participantError) return participantError;
    }

    const normalizedParams = { groupName, participantChatIds };

    // Thin-proxy contract: waCreateGroupTool now resolves to the canonical
    // BtSlimGroup (WaToolResult<...waCreateGroupTool>), same shape
    // waGetGroupDataTool returns — not the legacy thin {chatId} record.
    type CreateGroupToolResult = WaToolResult<typeof WA_TOOL_COMMANDS.waCreateGroupTool>;

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waCreateGroupTool(normalizedParams);
        return unwrapEngineResult(result);
    }

    return await triggerRequesthWhatsappTool<CreateGroupParams, CreateGroupToolResult>(WA_TOOL_COMMANDS.waCreateGroupTool,
        normalizedParams,
    );
}

// ============================================================================
// Group mutation tools — shared dual-mode factory
// ============================================================================
// Mirrors `createChatTool` in chat/chat-tools.ts. Every group tool below
// repeats the same shape: normalizeGroupId → validateChatIdForTool
// ({requireGroup:true}) → optional extra param prep/validation → extension:
// client action + isWireError→waToolError / waToolSuccess(result ?? {}) ;
// server: triggerRequesthWhatsappTool(command, params). The `prepare` hook is
// the per-tool pre-processing (required-field checks, participant validation,
// settings munging); it returns either a built params object or an early
// error response. `noResponseGuard` reproduces waGetGroupDataTool's original
// undefined→NO_RESPONSE guard (the only tool that had one).

type GroupTool<C extends WaToolCommand> = (
    params: WaToolArgs<C>,
) => Promise<WaToolResponse<WaToolResult<C>>>;

type GroupPrepared = { error: WaToolResponse<never> } | { params: Record<string, any> };

const createGroupTool = <C extends WaToolCommand>(
    command: C,
    clientAction: (params: any) => Promise<any>,
    prepare: (groupId: string, params: WaToolArgs<C>) => Promise<GroupPrepared> = async (groupId) => ({ params: { groupId } }),
    opts: { noResponseGuard?: boolean } = {},
): GroupTool<C> => async (params) => {
    const groupId = normalizeGroupId((params as any)?.groupId);

    const groupIdError = await validateChatIdForTool(groupId, { requireGroup: true });
    if (groupIdError) return groupIdError;

    const prepared = await prepare(groupId, params);
    if ('error' in prepared) return prepared.error;
    const normalizedParams = prepared.params;

    if (isWhatsappExtensionContext()) {
        const result = await clientAction(normalizedParams);
        if (opts.noResponseGuard && result === undefined) {
            return waToolError('engine_error', `No response from page context for ${command}`);
        }
        // An unguarded missing response keeps its old meaning (an empty success).
        return unwrapEngineResult((result ?? {}) as WireResponse<WaToolResult<C>>);
    }

    return await triggerRequesthWhatsappTool<Record<string, any>, WaToolResult<C>>(command, normalizedParams);
};

// Shared participant-validation prep for add/remove/admin tools.
const prepareParticipant = async (groupId: string, params: any): Promise<GroupPrepared> => {
    const participantChatId = normalizeParticipantChatId(params?.participantChatId);
    const participantError = await validateChatIdForTool(participantChatId);
    if (participantError) return { error: participantError };
    return { params: { groupId, participantChatId } };
};

export const waUpdateGroupNameTool: GroupTool<typeof WA_TOOL_COMMANDS.waUpdateGroupNameTool> = createGroupTool(
    WA_TOOL_COMMANDS.waUpdateGroupNameTool,
    (p) => WhatsappToolsClientActions.waUpdateGroupNameTool(p),
    async (groupId, params) => {
        const groupName = String(params?.groupName || '').trim();
        if (!groupName) return { error: waToolError('invalid_input', 'groupName is required') };
        return { params: { groupId, groupName } };
    },
);

export const waGetGroupDataTool: GroupTool<typeof WA_TOOL_COMMANDS.waGetGroupDataTool> = createGroupTool(
    WA_TOOL_COMMANDS.waGetGroupDataTool,
    (p) => WhatsappToolsClientActions.waGetGroupDataTool(p),
    undefined,
    { noResponseGuard: true },
);

export const waUpdateGroupDescriptionTool: GroupTool<typeof WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool> = createGroupTool(
    WA_TOOL_COMMANDS.waUpdateGroupDescriptionTool,
    (p) => WhatsappToolsClientActions.waUpdateGroupDescriptionTool(p),
    async (groupId, params) => {
        const settings = (params?.settings && typeof params.settings === 'object' && !Array.isArray(params.settings))
            ? params.settings
            : {};
        return { params: { groupId, settings } };
    },
);

export const waAddGroupParticipantTool: GroupTool<typeof WA_TOOL_COMMANDS.waAddGroupParticipantTool> = createGroupTool(
    WA_TOOL_COMMANDS.waAddGroupParticipantTool,
    (p) => WhatsappToolsClientActions.waAddGroupParticipantTool(p),
    prepareParticipant,
);

export const waRemoveGroupParticipantTool: GroupTool<typeof WA_TOOL_COMMANDS.waRemoveGroupParticipantTool> = createGroupTool(
    WA_TOOL_COMMANDS.waRemoveGroupParticipantTool,
    (p) => WhatsappToolsClientActions.waRemoveGroupParticipantTool(p),
    prepareParticipant,
);

export const waSetGroupAdminRightsTool: GroupTool<typeof WA_TOOL_COMMANDS.waSetGroupAdminRightsTool> = createGroupTool(
    WA_TOOL_COMMANDS.waSetGroupAdminRightsTool,
    (p) => WhatsappToolsClientActions.waSetGroupAdminRightsTool(p),
    prepareParticipant,
);

export const waRemoveGroupAdminRightsTool: GroupTool<typeof WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool> = createGroupTool(
    WA_TOOL_COMMANDS.waRemoveGroupAdminRightsTool,
    (p) => WhatsappToolsClientActions.waRemoveGroupAdminRightsTool(p),
    prepareParticipant,
);

export const waSetGroupPictureTool: GroupTool<typeof WA_TOOL_COMMANDS.waSetGroupPictureTool> = createGroupTool(
    WA_TOOL_COMMANDS.waSetGroupPictureTool,
    (p) => WhatsappToolsClientActions.waSetGroupPictureTool(p),
    async (groupId, params) => {
        const fileDataUrl = String(params?.fileDataUrl || '').trim();
        if (!fileDataUrl) return { error: waToolError('invalid_input', 'fileDataUrl is required') };
        return {
            params: {
                groupId,
                fileDataUrl,
                fileName: params?.fileName,
                fileMimeType: params?.fileMimeType,
            },
        };
    },
);

export const waLeaveGroupTool: GroupTool<typeof WA_TOOL_COMMANDS.waLeaveGroupTool> = createGroupTool(
    WA_TOOL_COMMANDS.waLeaveGroupTool,
    (p) => WhatsappToolsClientActions.waLeaveGroupTool(p),
);

// ============================================================================
// Group / Newsletter listing + newsletter mutation tools
// ============================================================================
// These four tools previously had NO thunk — the notifier (wa-tools-notifier-
// handlers.ts) called the client action directly and hand-rolled the error
// wrapping, an architecture violation (the notifier must be a pure proxy).
// The extension-branch logic here is byte-identical to that former inline
// notifier code so wire shapes are unchanged; the added server branch routes
// through MQTT like every other dual-mode tool.

export async function waGetNewsletterTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetNewsletterTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetNewsletterTool>>> {
    // Contract: `{newsletterId: string}`. Bare-string fallback dropped.
    const newsletterId = String((params as any)?.newsletterId ?? '').trim();
    if (!newsletterId) {
        return waToolError('invalid_input', 'newsletterId is required');
    }
    // A group id (`@g.us`) resolves to no newsletter, so the engine would
    // silently return `null` rather than an error — reject up-front so the
    // caller gets a clear validation failure instead of an empty success.
    if (chatKindOf(newsletterId) !== 'channel') {
        return waToolError('invalid_input', 'newsletterId must be a newsletter ID (ending with @newsletter)');
    }
    if (isWhatsappExtensionContext()) {
        return unwrapEngineResult(await WhatsappToolsClientActions.waGetNewsletterTool({ newsletterId }));
    }
    return await triggerRequesthWhatsappTool<Record<string, any>, WaToolResult<typeof WA_TOOL_COMMANDS.waGetNewsletterTool>>(
        WA_TOOL_COMMANDS.waGetNewsletterTool, { newsletterId },
    );
}

export async function waCreateNewsletterTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waCreateNewsletterTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waCreateNewsletterTool>>> {
    const name = String((params as any)?.name ?? '').trim();
    if (!name) {
        return waToolError('invalid_input', 'name is required');
    }
    const description = (params as any)?.description ?? null;
    if (isWhatsappExtensionContext()) {
        return unwrapEngineResult(await WhatsappToolsClientActions.waCreateNewsletterTool({ name, description }));
    }
    return await triggerRequesthWhatsappTool<Record<string, any>, WaToolResult<typeof WA_TOOL_COMMANDS.waCreateNewsletterTool>>(
        WA_TOOL_COMMANDS.waCreateNewsletterTool, { name, description },
    );
}


// ============================================================================
// Unified Contact & Chat Query Tool
// ============================================================================
// Single source of truth for all contact/chat queries using boolean flags:
// - includeContacts: true/false → Include contacts in result
// - includeChats: true/false → Include chats in result
// - filter.search → Search within results
// ============================================================================


// The inbound `params` are already the validated flat contract args
// (WaToolArgs), so no per-field re-normalization is needed — the page and
// backend handlers apply the same defaults regardless of which keys are
// present. Pass them straight through.
// #803: the campaigns surface mounts several components that each fire the same warm-up
// query ({}); share one in-flight page-world round-trip between identical concurrent calls.
const shareContactsAndChatsQuery = createInFlightShare<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waQueryContactsAndChatsTool>>>();

export async function waQueryContactsAndChatsTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waQueryContactsAndChatsTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waQueryContactsAndChatsTool>>> {
    const queryParams: WaToolArgs<typeof WA_TOOL_COMMANDS.waQueryContactsAndChatsTool> = params ?? {};

    if (isWhatsappExtensionContext()) {
        return shareContactsAndChatsQuery(
            JSON.stringify(queryParams),
            async () => unwrapEngineResult(await WhatsappToolsClientActions.waQueryContactsAndChatsTool(queryParams)),
        );
    } else {
        // Server mode: use backend action via MQTT
        return triggerRequesthWhatsappTool<QueryContactsAndChatsParams, QueryContactsAndChatsResult>(WA_TOOL_COMMANDS.waQueryContactsAndChatsTool, queryParams);
    }
}

export async function waGetLatestChatsAndMessagesTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool>>> {
    const queryParams: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool> = params ?? {};

    if (isWhatsappExtensionContext()) {
        return unwrapEngineResult(await WhatsappToolsClientActions.waGetLatestChatsAndMessagesTool(queryParams));
    } else {
        // Server mode: use backend action via MQTT
        return triggerRequesthWhatsappTool<GetLatestChatsAndMessagesParams, WaToolResult<typeof WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool>>(WA_TOOL_COMMANDS.waGetLatestChatsAndMessagesTool, queryParams);
    }
}

/**
 * Read the client-side IndexedDB message-vault (all chats, newest first,
 * cursor-paged). Extension mode reads the local vault directly via the page
 * bridge; server mode (website) routes over MQTT to the engine that owns the
 * vault (the vault lives on the web.whatsapp.com origin, unreachable from the
 * website origin). Powers the /view-backup "Vault" tab and the vault tier of
 * the unified message read.
 */
export async function waQueryVaultMessagesTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waQueryVaultMessagesTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waQueryVaultMessagesTool>>> {
    const queryParams = params ?? {};

    if (isWhatsappExtensionContext()) {
        return unwrapEngineResult(await WhatsappToolsClientActions.waQueryVaultMessagesTool(queryParams));
    } else {
        return triggerRequesthWhatsappTool<
            WaToolArgs<typeof WA_TOOL_COMMANDS.waQueryVaultMessagesTool>,
            WaToolResult<typeof WA_TOOL_COMMANDS.waQueryVaultMessagesTool>
        >(WA_TOOL_COMMANDS.waQueryVaultMessagesTool, queryParams);
    }
}

// ============================================================================
// Unified Message Query Tool
// ============================================================================
// Single source of truth for all message queries:
// - Get messages from a chat (history/latest modes)
// - Search messages by query
// - Paginated message retrieval
// ============================================================================

// Contract Args are the flat shape. The legacy nested form
// (`{search:{...}, pagination:{...}, dateFilter:{...}, options:{...}}`)
// has been collapsed — internal callers either now pass the flat form
// or wrap their nested shape before calling.
export async function waQueryMessagesTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waQueryMessagesTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waQueryMessagesTool>>> {
    // Args pass through untouched (plan 3.7): the engine applies the contract
    // rules (resolveLimit, mode/offset, filters) — no whitelist here.
    const queryParams: WaToolArgs<typeof WA_TOOL_COMMANDS.waQueryMessagesTool> = params ?? {};

    if (isWhatsappExtensionContext()) {
        const isSourceWa = params?.isSourceWa !== false;   // default true
        const isSourceVault = params?.isSourceVault !== false; // default true

        const result = await WhatsappToolsClientActions.waQueryMessagesTool(queryParams);
        if (isWireError(result)) return unwrapEngineResult(result);

        // Live WhatsApp store — gated by isSourceWa. A live row carries no
        // `source` (absent = live, plan A.9.2); vault rows carry source:'vault'.
        const rawLive: any[] = Array.isArray((result as any)?.messages) ? (result as any).messages : [];
        const liveMessages: any[] = isSourceWa ? rawLive : [];

        // ── Vault tier — gated by isSourceVault ─────────────────────────
        // A `latest` page reaching past the live store continues into the
        // vault's older rows (vaultTopUp: one timeline, offset-exact paging,
        // rows ascending). With WA off + vault on this yields vault-only — the
        // debug toggles' point. vaultBrowseWindow says when it applies.
        const limit = resolveLimit(WA_TOOL_COMMANDS.waQueryMessagesTool, 'limit', queryParams.limit);
        const chatId = queryParams.chatId;
        const vaultWindow = isSourceVault ? vaultBrowseWindow(queryParams) : null;
        if (chatId && vaultWindow) {
            try {
                const { messages, vaultMayHaveMore, total } = await vaultTopUp({
                    live: liveMessages,
                    liveTotal: isSourceWa ? ((result as any)?.totalMessages ?? rawLive.length) : 0,
                    offset: queryParams.offset ?? 0,
                    limit,
                    ...vaultWindow,
                    oldestLiveT: async () => {
                        const oldest = await WhatsappToolsClientActions.waQueryMessagesTool({ chatId, mode: 'history', limit: 1 });
                        return isWireError(oldest) ? undefined : oldest?.messages?.[0]?.t;
                    },
                    // Vault rows leave as the ONE message row shape (source:'vault').
                    queryVault: async (q) => (await queryVaultMessages({ chatId, ...q })).map(vaultRowToMessageRow),
                    countVault: (q) => countVaultMessages({ chatId, ...q }),
                });
                const paging = withVaultPaging((result as any)?.paging, vaultMayHaveMore);
                return waToolSuccess({
                    ...(result as any),
                    messages,
                    // totalMessages counts the same timeline the rows page over.
                    ...(total !== undefined ? { totalMessages: total } : {}),
                    ...(paging ? { paging } : {}),
                } as any);
            } catch (err) {
                console.warn('[bt-vault] query-messages vault fallback failed:', err);
            }
        }
        return waToolSuccess({ ...(result as any), messages: liveMessages } as any);
    } else {
        const result = await triggerRequesthWhatsappTool<QueryMessagesParams, QueryMessagesResult>(WA_TOOL_COMMANDS.waQueryMessagesTool, queryParams);
        return result;
    }
}

// ── Load Messages From Phone Tool ─────────────────────────────────────────────
// Standalone tool to load older messages from phone for a given chat

export async function waLoadMessagesFromPhoneTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool>>> {
    const { chatId } = params;

    // No `validateChatIdForTool` pre-flight here: this read-only history fetch
    // operates on an existing chat and the inner handler already fails for a
    // missing chat, so the extra round-trip to waValidatePhoneNumberOrChatIdTool
    // is pure overhead. Empty input is still rejected below.
    if (!chatId || !String(chatId).trim()) {
        return waToolError('invalid_input', 'chatId is required');
    }

    if (isWhatsappExtensionContext()) {
        // Extension mode: Direct communication via WhatsappToolsClientActions
        const result = await WhatsappToolsClientActions.waLoadMessagesFromPhoneTool(chatId ?? '');
        if (!result) {
            return waToolError('engine_error', 'No response from loadMessagesFromPhone');
        }
        if (isWireError(result)) return unwrapEngineResult(result);
        const payload: LoadMessagesFromPhoneResult = {
            totalMessages: typeof result.totalMessages === 'number' ? result.totalMessages : 0,
            totalMessagesAddedByLoadFromPhoneIfNeeded: typeof result.totalMessagesAddedByLoadFromPhoneIfNeeded === 'number' ? result.totalMessagesAddedByLoadFromPhoneIfNeeded : 0,
            canLoadMore: !!result.canLoadMore,
            // Forward the phone-history-unavailable reason. Without this the
            // page handler's `error` was dropped here, so the tool reported a
            // clean success and the /v1/messages/load_older API never saw
            // `history_unavailable`.
            ...(result.error ? { error: result.error } : {}),
        };
        return waToolSuccess(payload);
    } else {
        // Server mode: Route through backend -> MQTT -> Engine -> WhatsApp
        const result = await triggerRequesthWhatsappTool<LoadMessagesFromPhoneParams, LoadMessagesFromPhoneResult>(WA_TOOL_COMMANDS.waLoadMessagesFromPhoneTool, { chatId: chatId ?? '' });
        if (!result) {
            return waToolError('engine_error', 'No response from server');
        }
        return result;
    }
}

export async function waGetMessageByKeyTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waGetMessageByKeyTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessageByKeyTool>>> {
    const waMessageKey = params?.waMessageKey;
    if (!waMessageKey) {
        return waToolError('invalid_input', 'waMessageKey is required');
    }

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.waGetMessageByKeyTool(waMessageKey);
        // Not found is `null` — a nullable read's success, not an error (A.9.1).
        return result === null ? { success: true, data: null } : unwrapEngineResult(result);
    }
    // Server mode: forward over MQTT to the engine.
    return triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waGetMessageByKeyTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waGetMessageByKeyTool>
    >(WA_TOOL_COMMANDS.waGetMessageByKeyTool, { waMessageKey });
}

// ============================================================================
// Message-key mutation tools — shared dual-mode factory
// ============================================================================
// react / pin / unpin all share: require waMessageKey → optional extra
// validation + param build → extension: client action + unwrapEngineResult ;
// server: triggerRequesthWhatsappTool(command, params) returned as-is.
// waGetMessageAckTool stays a standalone function (defined far earlier, so a
// shared const here would hit a TDZ), and waGetMessageByKeyTool keeps its
// bespoke NOT_FOUND/FETCH_FAILED normalizer.
type MessageKeyTool<C extends WaToolCommand> = (
    params: WaToolArgs<C>,
) => Promise<WaToolResponse<WaToolResult<C>>>;

const createMessageKeyTool = <C extends WaToolCommand>(
    command: C,
    clientAction: (params: any) => Promise<any>,
    buildParams: (waMessageKey: string, params: WaToolArgs<C>) => Record<string, any> = (waMessageKey) => ({ waMessageKey }),
    validateExtra?: (params: WaToolArgs<C>) => WaToolResponse<never> | null,
): MessageKeyTool<C> => async (params) => {
    const waMessageKey = (params as any)?.waMessageKey;
    if (!waMessageKey) {
        return waToolError('invalid_input', 'waMessageKey is required');
    }
    if (validateExtra) {
        const extraError = validateExtra(params);
        if (extraError) return extraError;
    }

    const normalizedParams = buildParams(waMessageKey, params);

    if (isWhatsappExtensionContext()) {
        const result = await clientAction(normalizedParams);
        return unwrapEngineResult(result);
    }
    return await triggerRequesthWhatsappTool<Record<string, any>, WaToolResult<C>>(command, normalizedParams);
};

export const waReactToMessageThunkTool: MessageKeyTool<typeof WA_TOOL_COMMANDS.waReactToMessageThunkTool> = createMessageKeyTool(
    WA_TOOL_COMMANDS.waReactToMessageThunkTool,
    (p) => WhatsappToolsClientActions.waReactToMessageThunkTool(p),
    (waMessageKey, params) => ({ waMessageKey, text: (params as any)?.text }),
    (params) => ((params as any)?.text === undefined || (params as any)?.text === null)
        ? waToolError('invalid_input', 'text is required')
        : null,
);

export const waForwardMessageTool: MessageKeyTool<typeof WA_TOOL_COMMANDS.waForwardMessageTool> = createMessageKeyTool(
    WA_TOOL_COMMANDS.waForwardMessageTool,
    (p) => WhatsappToolsClientActions.waForwardMessageTool(p),
    (waMessageKey, params) => ({
        waMessageKey,
        toChatIds: (params as any)?.toChatIds,
        chatId: (params as any)?.chatId,
    }),
    (params) => (!Array.isArray((params as any)?.toChatIds) || (params as any).toChatIds.length === 0)
        ? waToolError('invalid_input', 'toChatIds must be a non-empty array')
        : null,
);

export const waStarMessageTool: MessageKeyTool<typeof WA_TOOL_COMMANDS.waStarMessageTool> = createMessageKeyTool(
    WA_TOOL_COMMANDS.waStarMessageTool,
    (p) => WhatsappToolsClientActions.waStarMessageTool(p),
    (waMessageKey, params) => ({
        waMessageKey,
        star: (params as any)?.star,
        chatId: (params as any)?.chatId,
    }),
    (params) => (typeof (params as any)?.star !== 'boolean')
        ? waToolError('invalid_input', 'star must be a boolean')
        : null,
);

export const waDeleteMessageTool: MessageKeyTool<typeof WA_TOOL_COMMANDS.waDeleteMessageTool> = createMessageKeyTool(
    WA_TOOL_COMMANDS.waDeleteMessageTool,
    (p) => WhatsappToolsClientActions.waDeleteMessageTool(p),
    (waMessageKey, params) => ({
        waMessageKey,
        forEveryone: (params as any)?.forEveryone,
        chatId: (params as any)?.chatId,
    }),
);

export const waPinMessageTool: MessageKeyTool<typeof WA_TOOL_COMMANDS.waPinMessageTool> = createMessageKeyTool(
    WA_TOOL_COMMANDS.waPinMessageTool,
    (p) => WhatsappToolsClientActions.waPinMessageTool(p),
    (waMessageKey, params) => ({ waMessageKey, duration: (params as any)?.duration }),
);

export const waUnpinMessageTool: MessageKeyTool<typeof WA_TOOL_COMMANDS.waUnpinMessageTool> = createMessageKeyTool(
    WA_TOOL_COMMANDS.waUnpinMessageTool,
    (p) => WhatsappToolsClientActions.waUnpinMessageTool(p),
);

const normalizeCreateStatusToolResult = (
    rawResult: any,
    fallbackAction: 'text' | 'media',
): WaToolResponse<{ action?: 'text' | 'media'; messageId?: string }> => {
    if (!rawResult) {
        return waToolError('engine_error', 'No response received for createStatusTool');
    }

    if (typeof rawResult !== 'object') {
        return waToolError('engine_error', `Invalid createStatusTool response type: ${typeof rawResult}`);
    }

    const payload = (rawResult?.data && typeof rawResult.data === 'object')
        ? rawResult.data
        : rawResult;

    if (payload?.success === false) {
        return waToolError('engine_error', typeof payload?.error === 'string' ? payload.error : 'Failed to create status');
    }

    const action: 'text' | 'media' =
        payload?.action === 'media'
            ? 'media'
            : payload?.action === 'text'
                ? 'text'
                : fallbackAction;

    if (
        payload?.success === true ||
        payload?.action !== undefined ||
        payload?.messageId !== undefined
    ) {
        return waToolSuccess({
            action,
            messageId: typeof payload?.messageId === 'string' ? payload.messageId : undefined,
        });
    }

    return waToolError('engine_error', 'createStatusTool returned an empty response');
};

export async function createStatusTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.createStatusTool> | undefined | null,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.createStatusTool>>> {
    const safeParams = (params && typeof params === 'object' ? params : {}) as CreateStatusParams;

    const normalizedText = String(safeParams.text || safeParams.content || '').trim();
    const normalizedFileDataUrl = String(safeParams.fileDataUrl || '').trim();

    if (!normalizedText && !normalizedFileDataUrl) {
        return waToolError('invalid_input', 'Either text or fileDataUrl is required');
    }

    const isDataUrl = /^data:/i.test(normalizedFileDataUrl);
    const isLargeDataUrl = normalizedFileDataUrl.length > 100_000;
    if (!isWhatsappExtensionContext() && isDataUrl && isLargeDataUrl) {
        return waToolError('invalid_input', 'createStatusTool server mode does not support large data URLs. Please upload the file and pass a public URL.');
    }

    const normalizedParams: CreateStatusParams = {
        supportedUserId: safeParams.supportedUserId,
        // FE#360 — carry the duplicate-guard tag; dropping it here would leave the
        // posted Status untagged and re-open the double-post.
        userMessageId: safeParams.userMessageId,
        text: normalizedText || undefined,
        content: normalizedText || undefined,
        fileDataUrl: normalizedFileDataUrl || undefined,
        fileName: safeParams.fileName,
        fileMimeType: safeParams.fileMimeType,
    };
    const fallbackAction: 'text' | 'media' = normalizedFileDataUrl ? 'media' : 'text';

    if (isWhatsappExtensionContext()) {
        const result = await WhatsappToolsClientActions.createStatusTool(normalizedParams);
        return normalizeCreateStatusToolResult(result, fallbackAction);
    } else {
        const result = await triggerRequesthWhatsappTool<CreateStatusParams, CreateStatusResult>(WA_TOOL_COMMANDS.createStatusTool, normalizedParams);
        return normalizeCreateStatusToolResult(result, fallbackAction);
    }
}

export async function waOpenChatTool(
    params: WaToolArgs<typeof WA_TOOL_COMMANDS.waOpenChatTool>,
): Promise<WaToolResponse<WaToolResult<typeof WA_TOOL_COMMANDS.waOpenChatTool>>> {
    const chatIdError = await validateChatIdForTool(params.chatId);
    if (chatIdError) return chatIdError;

    if (isWhatsappExtensionContext()) {
        // Extension mode: call WhatsappToolsClientActions
        try {
            await WhatsappToolsClientActions.openChatById(params.chatId);
            return waToolSuccess({ chatId: params.chatId });
        } catch (error: any) {
            return waToolError('engine_error', error.message || 'Failed to open chat');
        }
    }

    return triggerRequesthWhatsappTool<
        WaToolArgs<typeof WA_TOOL_COMMANDS.waOpenChatTool>,
        WaToolResult<typeof WA_TOOL_COMMANDS.waOpenChatTool>
    >(WA_TOOL_COMMANDS.waOpenChatTool, { chatId: params.chatId });
}
